/**
 * The imperative editor controller — the pointer-driven half of the plan
 * editor, owning the SVG DOM inside a React shell exactly the way `MapCanvas`
 * owns geoman's (spec 2026-08-22-02 §2). React never re-renders on a pointer
 * move; it subscribes to the coarse events below.
 *
 * World space is millimetres, Y-down; the view maps it to screen with
 * `screen = mm·k + offset`, so snapping tolerances given in screen pixels stay
 * zoom-invariant (§8).
 */
import {
    IDENTITY,
    applyTransform,
    bboxOfPoints,
    clamp,
    dist,
    invertTransform,
    projectToLine,
    projectToSegment,
    toDeg,
    formatDistanceMm,
    type BBox,
    type Point,
    type Transform,
    centroid,
} from './geometry'
import {
    calibrationFromMeasure,
    calibrationFromPairs,
    calibrationModeFor,
    measureScaleTransform,
    mmPerUnit,
    mmToUnits as calMmToUnits,
    clampFootprint,
    MAX_OPENING_FRACTION,
    MAX_THICKNESS_FRACTION,
    unitsPerMm as calUnitsPerMm,
    unitsToMm as calUnitsToMm,
    type LengthContext,
    rotateAbout,
    solveSimilarity,
    translateBy,
    type AnchorPair,
    type CalibrationCommit,
    type FrameCalibration,
} from './calibrate'
import { History } from './history'
import {
    DEFAULT_DOOR_WIDTH,
    DEFAULT_WALL_THICKNESS,
    DEFAULT_WINDOW_WIDTH,
    type OpeningKind,
    type Operation,
    type Swing,
} from './openings'
import {
    LAYER_IDS,
    defaultLayerStates,
    equipmentFootprintCorners,
    equipmentFootprintOffset,
    equipmentSizeAt,
    esc,
    renderWorld,
    ringPath,
    type LayerId,
    type LayerStates,
    grouperIdOf,
} from './render'
import {
    applyLayerTransform,
    applySceneCalibration,
    bundleTargets,
    canPromote,
    cloneDoc,
    collectLocalIds,
    containingSpace,
    demoteElement,
    documentPoints,
    elementByKey,
    elementPoints,
    layerBBox,
    layerPoints,
    layerTransform,
    nextKey,
    nextLocalId,
    parentSpaceKeyOf,
    promoteElement,
    repAssetUidOf,
    retargetWall,
    revertSceneCalibration,
    sceneLayers,
    type BundleTarget,
    type EditorElement,
    type ElementOwner,
    containingGrouper,
    groupersAt,
    grouperRefOf,
    holeVertexIndex,
    isGrouperElement,
    removeHole,
    spaceForHole,
    spaceHoles,
    type GrouperRef,
    type OpeningElement,
    type SceneDoc,
    type SceneLayer,
    type NetworkLevel,
    type RiserElement,
    type RiserPassage,
    type RunElement,
    type SpaceElement,
    type WallElement,
} from './scene'
import {
    nearestWall,
    openingPlacement,
    snapPoint,
    wallDirectionDeg,
    OPENING_PICK_TOLERANCE_PX,
    SNAP_TOLERANCE_PX,
} from './snap'
import {
    categoryLayer,
    dominantCategory,
    elementInsideBox,
    sameCategory,
    selectionCategory,
} from './selection'
import { validate, type ValidationIssue } from './validation'

export type ToolId = 'select' | 'wall' | 'room' | 'unit' | 'hole' | 'door' | 'window' | 'equipment' | 'run' | 'riser' | 'calibrate' | 'pan'

/**
 * Arrow-key nudge, in screen pixels: a nudge is a gesture, and a plan unit is
 * worth anything from a millimetre to 80 metres, so a plan-unit step is
 * either invisible or a leap. Divided by the zoom, like `MIN_SEGMENT_PX`.
 */
const NUDGE_PX = 1
const NUDGE_COARSE_PX = 10
/** Arrow-key pan, in screen pixels. */
const PAN_STEP_PX = 60
/**
 * How far the pointer may drift and still count as "the same spot" when
 * walking overlapping groupers (spec 2026-08-30-03 §7). Generous enough to
 * survive the hand-tremor of a double click, tight enough that moving to a
 * different room starts a new question.
 */
const AMBIGUITY_TOLERANCE_PX = 6
/**
 * Our own double-click window. The browser's `dblclick` never arrives on this
 * stage — the pointer is captured on `pointerdown` and the layers are rebuilt
 * under the cursor between the two clicks, and Chromium then declines to pair
 * them — so "double-click to close a polygon" and "double-click an edge to
 * insert a vertex" were both dead letters. The engine pairs the clicks itself.
 */
const DOUBLE_CLICK_MS = 350
const DOUBLE_CLICK_PX = 6

/**
 * When a press *happened*, in epoch ms — the event's own timestamp, not the
 * time its handler got to run. The first click of a pair selects, and the
 * render that follows can hold the main thread past the whole double-click
 * window on a slow machine; timed by the handler, a double click typed in
 * 100 ms then reads as two singles. Synthetic events without a timestamp fall
 * back to the clock.
 */
const pressedAt = (e: PointerEvent): number =>
    e.timeStamp > 0 ? performance.timeOrigin + e.timeStamp : Date.now()

/** The tools that create something, and so are set up before the first click. */
export const CREATION_TOOLS: ToolId[] = ['wall', 'room', 'unit', 'hole', 'door', 'window', 'equipment', 'run', 'riser']

/** A hole's handle: `h<hole>v<vertex>` on a corner, `h<hole>m<edge>` mid-edge. */
const HOLE_HANDLE = /^h(\d+)([vm])(\d+)$/

/**
 * What the stage is looking at, in document terms: the millimetre point under
 * the middle of the viewport, and the scale in pixels per millimetre.
 */
export interface PlanViewport {
    cx: number
    cy: number
    k: number
}

/** What the shell mirrors of the calibrate mode (spec 2026-08-22-03 §1, §2). */
export interface CalibrationState {
    /** The registration being calibrated, null when none is picked yet. */
    registrationUid: string | null
    pairs: AnchorPair[]
    /** True between the two clicks of a pair. */
    awaitingTarget: boolean
    /** True while the measure tool waits for its two points. */
    measuring: boolean
    /** True once a pair or a measurement changed the layer's placement. */
    dirty: boolean
    /**
     * What "apply" will write (spec 2026-08-25-04 §3):
     * - `calibration` — the target is the frame's **primary plan**: a
     *   `calibration` PATCH, and nothing moves;
     * - `placement` — any other layer: the transform PATCH it always was.
     * The panel's copy switches on this, so a user never reads "calibrate"
     * while the editor is about to move their drawing.
     */
    mode: 'calibration' | 'placement'
    /** The frame's live millimetres-per-plan-unit, null when not to scale. */
    mmPerUnit: number | null
    /**
     * The calibration the frame is *carrying* — the stored one, or the pending
     * edit once there is one (spec 2026-09-06-01 §3). The panel reads its
     * method and audit trail from here rather than from the document, and the
     * stage draws it so entering calibrate mode on a calibrated plan shows
     * what was measured instead of an empty canvas.
     */
    current: FrameCalibration | null
    /** Whether that committed calibration is drawn on the stage. */
    showCurrent: boolean
}

/**
 * Zoom limits are expressed **relative to the scale at which the whole
 * document fits the viewport**, not as absolute screen-pixels-per-millimetre.
 *
 * A frame's extent legitimately spans several orders of magnitude: an
 * uncalibrated raster underlay's local frame is the crop's unit square (~1 mm
 * across, so it fits at ~200 px/mm) while a calibrated floor is ~20 m (fits at
 * ~0.015 px/mm). Any fixed px/mm range is therefore unreachable at one end or
 * the other — a document would open outside its own zoom range, and the first
 * zoom step would snap it violently to the nearest bound and stick there.
 */
export const MIN_ZOOM_REL = 0.05
export const MAX_ZOOM_REL = 400
/** Fraction of the viewport the document occupies once fitted. */
export const FIT_MARGIN = 0.92

/** Viewport size in screen pixels — the part of a DOMRect the view math needs. */
export interface Viewport {
    width: number
    height: number
}

/**
 * The scale at which `bb` — or an empty scene's default 20 m of drawing room —
 * exactly fills `vp`. This is the reference every zoom limit is measured
 * against, and the scale `fitView` settles on (times {@link FIT_MARGIN}).
 */
export function fitScale(vp: Viewport, bb: BBox | null): number {
    if (!bb) return Math.min(vp.width, vp.height) / 20000
    const w = Math.max(bb.x1 - bb.x0, 1)
    const h = Math.max(bb.y1 - bb.y0, 1)
    return Math.min(vp.width / w, vp.height / h)
}

/** The reachable zoom range around a fit scale. */
export function zoomRange(fit: number): { min: number; max: number } {
    return { min: fit * MIN_ZOOM_REL, max: fit * MAX_ZOOM_REL }
}

/** What a fit needs: the scale, and the box dimensions it centres on. */
export interface FitBox {
    k: number
    w: number
    h: number
}

/**
 * The scale and the dimensions a fit centres on.
 *
 * `tight: false` is `fitScale`'s own maths, down to the `Math.max(…, 1)` floor,
 * and is what every frame-rooted view uses.
 *
 * `tight: true` is for a box that is a REGION of the document rather than the
 * whole of it — a lot inside its floor's page (spec 2026-09-17-01 §3). That
 * one-world-unit floor is a sane 1 mm on a calibrated frame and the WHOLE PAGE
 * on one that is not, whose coordinates are the crop's unit square, so a region
 * measures its degenerate guard against its own larger side instead.
 *
 * A box with NO extent in either axis — a subject whose only drawn element is a
 * single equipment or opening, one point — has no larger side to measure
 * against, and a relative guard would be exactly zero: `vp.width / 0` is
 * Infinity and the offsets computed from it are NaN, i.e. a blank canvas. Such
 * a box falls back to the floored maths, which is the only thing here that
 * cannot divide by zero.
 */
export function fitBox(vp: Viewport, bb: BBox, tight = false): FitBox {
    const rawW = bb.x1 - bb.x0
    const rawH = bb.y1 - bb.y0
    const span = Math.max(rawW, rawH)
    const guard = tight && span > 0 ? span * 1e-3 : 1
    const w = Math.max(rawW, guard)
    const h = Math.max(rawH, guard)
    return { k: Math.min(vp.width / w, vp.height / h), w, h }
}
/**
 * Wall chain segments shorter than this on screen are dropped as mis-clicks.
 * Screen pixels, not plan units: a plan unit is whatever the frame's
 * `mm_per_unit` says, and on an image-based plan it is tens of metres.
 */
const MIN_SEGMENT_PX = 10

/** What a copied equipment carries: geometry and type, never identity. */
export interface ClipboardItem {
    /**
     * What was copied. Absent means equipment — every clipboard written before
     * openings could be copied holds equipment, and one lives in
     * `sessionStorage` across reloads.
     */
    kind?: 'equipment' | 'opening'
    typeCode?: string
    width: number
    depth: number
    rotationDeg: number
    /** Openings only: everything that makes a door a door (spec §14). */
    openingKind?: OpeningKind
    wallThickness?: number
    operation?: Operation
    swing?: Swing
    leaves?: 1 | 2
    rotation?: 0 | 180
    /** Offset from the copied group's anchor, so a paste keeps the layout. */
    dx: number
    dy: number
}

export interface ViewState {
    k: number
    x: number
    y: number
}

export interface EditorHooks {
    /** The document changed (any mutation, undo, redo, external set). */
    onChange: (dirty: boolean) => void
    onSelectionChange: (key: string | null, extraKeys?: string[]) => void
    /**
     * The active tool changed. The engine owns it — the keyboard shortcuts
     * (V/W/R/D/N/E/C) go straight to `setTool` — so without this the shell's
     * mirror goes stale: the toolbar keeps highlighting the old tool, the stage
     * keeps its cursor, and the panels that belong to a tool never open.
     */
    onToolChange?: (tool: ToolId) => void
    /** A room polygon was closed — the shell focuses its name field. */
    onRoomCreated: (key: string) => void
    /** A hole was cut out of a space (spec 2026-10-19-07), which is now selected. */
    onHoleCut?: (spaceKey: string) => void
    /**
     * A traced hole fits in no space — outside every room, across one's edge,
     * or over a hole already there — and was dropped. The shell says why: a
     * ring that vanishes without a word reads as a bug.
     */
    onHoleRejected?: () => void
    /** An equipment was dropped — the shell opens the type picker on it. */
    onEquipmentPlaced: (key: string) => void
    /** A new footprint was scaled down to fit the drawing (plan not calibrated or scale off). */
    onFootprintReduced?: () => void
    /**
     * An equipment finished being dragged. The shell compares where it landed
     * with what it is filed under, and offers to re-parent it when the two no
     * longer agree — moving a radiator into another room is the moment to ask,
     * and the only moment the user can answer without hunting for the field.
     */
    onEquipmentMoved?: (key: string) => void
    /** `Cmd/Ctrl+S` — the keyboard's way of pressing Save. */
    onSaveRequested?: () => void
    /** A space was double-clicked: the shell puts the cursor in its name. */
    onRenameRequested?: (key: string) => void
    /**
     * Something was copied. The shell persists it, so a copy on one floor can
     * be pasted on the next — the clipboard belongs to the session, not to one
     * mounted editor.
     */
    onClipboardChange?: (items: ClipboardItem[]) => void
    /**
     * The viewport moved. Fires on the *change*, not on every render, and the
     * shell is the one that decides when the movement has settled enough to be
     * worth writing down.
     */
    onViewChange?: (view: PlanViewport) => void
    /**
     * What the equipment tool is armed with, read at the moment of the click.
     * The type is chosen **before** placing, so the drop lands as the right
     * thing straight away instead of as an untyped box awaiting a dialog.
     */
    equipmentDefaults?: () => { typeCode?: string; width?: number; depth?: number }
    /**
     * What the run tool is armed with, read when a run is finished: the
     * network it belongs to and, optionally, its real width in millimetres.
     * Chosen before drawing, like the equipment type.
     */
    runDefaults?: () => { network?: string; widthMm?: number; level?: NetworkLevel }
    /** A run was finished — the shell selects it in the inspector. */
    onRunCreated?: (key: string) => void
    /**
     * What the riser tool is armed with, read at the click: the network, the
     * real diameter and the passage (spec 2026-10-19-03 §1). The column is
     * linked afterwards, in the inspector.
     */
    riserDefaults?: () => {
        network?: string
        diameterMm?: number
        passage?: RiserPassage
    }
    /** A riser was placed — the shell selects it in the inspector. */
    onRiserCreated?: (key: string) => void
    /** Localizes a network code, for the run tooltips. */
    networkLabelOf?: (code: string) => string
    /** Owner resolution for a newly created element (ontology lives in React). */
    createOwner: (
        kind: 'space' | 'grouper' | 'opening' | 'equipment',
        point: Point,
        hint?: {
            openingKind?: OpeningKind
            typeCode?: string
            /**
             * The asset owning the wall an opening is cut into — the openings
             * carve-out of spec 2026-08-25-04 §2, which parents there rather
             * than to whichever room the centre happens to fall in (it falls
             * *on* a room boundary by construction).
             */
            wallOwnerUid?: string
            /** The host wall's thickness — the scale a window's room is judged at. */
            wallThickness?: number
        },
    ) => ElementOwner
    /** Owner of freshly drawn walls — the toolbar's "drawing into" target. */
    wallOwner: () => ElementOwner
    /** Existing representation new walls should join, if the target already has one. */
    wallRepFor: (owner: ElementOwner) => { repUid?: string; registrationUid?: string; transform: typeof IDENTITY } | null
    underlayHref: (repUid: string) => string
    /**
     * Localizes an asset type code, so an unnamed room can be drawn as
     * "Chambre 2" (spec 2026-08-31-02 §2). The catalog lives in the shell; the
     * engine only passes it through to the renderer.
     */
    typeLabelOf?: (code: string) => string
    /** The calibrate mode changed — the HUD panel mirrors it (§1). */
    onCalibrationChange?: (state: CalibrationState) => void
    /**
     * Whether a tool may be armed at all on this frame. A tool whose every
     * product the API refuses here — the grouper in a lot's own editor — is
     * refused at arming, so the keyboard shortcut cannot reach what the
     * toolbar disables (spec 2026-09-23-02). Absent: every tool is available.
     */
    toolAvailable?: (tool: ToolId) => boolean
    /** Two measure points are in: the shell asks for the real distance (§2). */
    onMeasureReady?: (a: Point, b: Point) => void
    /** A layer (not an element) was selected for direct manipulation (§6). */
    onLayerSelectionChange?: (registrationUid: string | null) => void
    /** Enter in calibrate mode: the shell writes the pending registration. */
    onCalibrationCommit?: () => void
}

interface DragState {
    type: 'view' | 'move' | 'handle' | 'layer' | 'marquee'
    x0: number
    y0: number
    moved: boolean
    vx?: number
    vy?: number
    deselectOnClick?: boolean
    key?: string
    handle?: string
    snapshot?: string
    orig?: EditorElement
    mm0?: Point
    /** Companions of a group move, and where each of them started. */
    companions?: { key: string; orig: EditorElement }[]
    /** Marquee drags: where the rubber band was pressed, in screen pixels. */
    mScr0?: Point
    /** Layer drags: the registration's transform when the drag started. */
    layerBase?: Transform
    layerPivot?: Point
    layerAngle0?: number
    /**
     * A mid-handle press already inserted a vertex: the gesture changed the
     * document even if the pointer never moves, so release must commit it.
     */
    inserted?: boolean
}

interface DrawingState {
    kind: 'wall' | 'room' | 'hole' | 'run'
    last?: Point
    pts?: Point[]
    cur?: Point
}

/** An element's reference point, for measuring how far a drag actually took it. */
function originOf(el: EditorElement): Point {
    switch (el.kind) {
        case 'wall': return el.start
        case 'space': return el.boundary[0] ?? { x: 0, y: 0 }
        case 'opening': return el.center
        case 'equipment': return el.position
        case 'run': return el.points[0] ?? { x: 0, y: 0 }
        case 'riser': return el.position
    }
}

/** Re-place an element at its pre-drag geometry plus a delta. */
function placeFrom(el: EditorElement, orig: EditorElement, delta: Point): void {
    if (el.kind === 'wall' && orig.kind === 'wall') {
        el.start = { x: orig.start.x + delta.x, y: orig.start.y + delta.y }
        el.end = { x: orig.end.x + delta.x, y: orig.end.y + delta.y }
    } else if (el.kind === 'space' && orig.kind === 'space') {
        el.boundary = orig.boundary.map((p) => ({ x: p.x + delta.x, y: p.y + delta.y }))
        if (orig.holes) el.holes = orig.holes.map((h) => h.map((p) => ({ x: p.x + delta.x, y: p.y + delta.y })))
    } else if (el.kind === 'opening' && orig.kind === 'opening') {
        el.center = { x: orig.center.x + delta.x, y: orig.center.y + delta.y }
    } else if (el.kind === 'equipment' && orig.kind === 'equipment') {
        el.position = { x: orig.position.x + delta.x, y: orig.position.y + delta.y }
    } else if (el.kind === 'run' && orig.kind === 'run') {
        el.points = orig.points.map((p) => ({ x: p.x + delta.x, y: p.y + delta.y }))
    } else if (el.kind === 'riser' && orig.kind === 'riser') {
        el.position = { x: orig.position.x + delta.x, y: orig.position.y + delta.y }
    }
}

export class PlanEditor {
    private svg: SVGSVGElement | null = null
    private world: SVGGElement | null = null
    private hud: SVGGElement | null = null
    private layerNodes = new Map<LayerId, SVGGElement>()

    private doc: SceneDoc
    private history = new History()
    private dirty = false

    view: ViewState = { k: 0.05, x: 0, y: 0 }
    layers: LayerStates = defaultLayerStates()
    tool: ToolId = 'select'
    selectedKey: string | null = null
    /**
     * Companions of the primary selection (shift-click). Kept separate so every
     * existing read of `selectedKey` keeps meaning "the one being edited", and
     * only the verbs that should act on a group look here as well.
     */
    extraKeys: string[] = []
    /** Copied equipment, waiting to be pasted. */
    clipboard: ClipboardItem[] = []
    /** Where the pointer last was over the stage — where a paste lands. */
    private lastPointerMm: Point | null = null
    /**
     * The element under the pointer, highlighted so the user can see what a
     * click would take before taking it (spec 2026-08-30-03 §8).
     */
    hoverKey: string | null = null
    /** The DOM node the last pointermove reported — the browser's own hit-test. */
    private hoverEventTarget: EventTarget | null = null
    /**
     * The element whose parent is blinking right now (§16). It is the answer to
     * a *click* on a room or an equipment — "and where does this belong?" — not
     * a property of the selection: focusing from the URL, creating, undoing,
     * walking an overlap or picking a unit all leave the drawing still. So
     * every `select()` clears it and only the pointer path sets it back, and
     * the first drag or edit that follows ends it.
     */
    private blinkKey: string | null = null
    /**
     * Overlapping groupers under one point, and how far through them the
     * repeated clicks have walked (§7). Kept until the pointer moves somewhere
     * else, which is what makes "click again for the next one" discoverable
     * without a modifier key.
     */
    private ambiguity: { scr: Point; keys: string[]; index: number } | null = null
    /** The previous click, for pairing double clicks ourselves. */
    private lastClick: { at: number; scr: Point } | null = null
    /** A double click seen on press, waiting for a release that did not drag. */
    private pendingDouble: { scr: Point; target: EventTarget | null } | null = null
    /** The last view `render` reported, so a document change does not re-report it. */
    private lastViewStamp = ''
    selectedVertex: number | null = null
    /** The hole `selectedVertex` is a corner of; `null` for a corner of the boundary. */
    selectedHole: number | null = null
    issues: ValidationIssue[] = []
    /** The transient segment `flashMeasure` draws, and its timer. */
    private flashSegment: { a: Point; b: Point } | null = null
    private flashTimer: number | null = null
    /**
     * A low-confidence auto-calibration's detected circle, as its horizontal
     * diameter in frame units (spec 2026-09-30-02 §4). Drawn until the user
     * accepts, measures by hand or cancels — unlike the flash, it waits.
     */
    private proposalSegment: { a: Point; b: Point } | null = null
    snapEnabled = true
    /**
     * Draw the committed calibration on the stage (spec 2026-09-06-01 §3). On
     * by default: the first question in calibrate mode is "what is it set to
     * now, and does it look right?".
     */
    showCurrentCalibration = true
    /** Grouping overlay visibility (spec 2026-08-30-01 §8). */
    showGroupers = true
    /** Network codes whose runs the layer panel hides. A display switch, never an edit. */
    hiddenNetworks: ReadonlySet<string> = new Set()

    /**
     * Sticky defaults, exactly like the exp editor's `lastThickness` family.
     * **Millimetres**: they are drawing habits ("100 mm walls"), converted to
     * plan units when they reach geometry (`mmToUnits`).
     */
    lastThickness = DEFAULT_WALL_THICKNESS
    lastDoorWidth = DEFAULT_DOOR_WIDTH
    lastWindowWidth = DEFAULT_WINDOW_WIDTH

    private altDown = false
    private shiftDown = false
    private spaceDown = false
    private drag: DragState | null = null
    private drawing: DrawingState | null = null
    private ghost: string | null = null
    private ghostWall: { wallKey: string; center: Point; directionDeg: number; thickness: number } | null = null
    private snapIndicator: Point | null = null
    /**
     * The rubber band being dragged, in screen pixels (spec 2026-09-06-01 §4).
     * HUD-only: it selects, it never edits, so nothing about it reaches the
     * document, the diff or the undo history.
     */
    private marquee: { a: Point; b: Point } | null = null

    /* --- calibration (spec 2026-08-22-03 §1/§2) and layer manipulation (§6) --- */
    /** The registration being calibrated, and the placement it started from. */
    private calibrationUid: string | null = null
    private calibrationBase: Transform | null = null
    private pairs: AnchorPair[] = []
    private pendingPair: { source: Point; frame: Point } | null = null
    private measure: { p1?: Point; cur?: Point } | null = null
    /** The layer selected for direct manipulation, in select mode. */
    selectedLayerUid: string | null = null

    private hooks: EditorHooks

    constructor(doc: SceneDoc, hooks: EditorHooks) {
        this.doc = doc
        this.hooks = hooks
        this.issues = validate(doc)
    }

    /* ------------------------------------------------------------- lifecycle */

    mount(svg: SVGSVGElement): void {
        this.svg = svg
        this.world = svg.querySelector('[data-world]') as SVGGElement | null
        this.hud = svg.querySelector('[data-hud]') as SVGGElement | null
        for (const id of LAYER_IDS) {
            const node = svg.querySelector(`[data-layer="${id}"]`) as SVGGElement | null
            if (node) this.layerNodes.set(id, node)
        }
        svg.addEventListener('pointerdown', this.onPointerDown)
        svg.addEventListener('pointermove', this.onPointerMove)
        svg.addEventListener('pointerup', this.onPointerUp)
        svg.addEventListener('pointercancel', this.onPointerUp)
        svg.addEventListener('wheel', this.onWheel, { passive: false })
        window.addEventListener('keydown', this.onKeyDown)
        window.addEventListener('keyup', this.onKeyUp)
        this.render()
    }

    destroy(): void {
        const svg = this.svg
        if (svg) {
            svg.removeEventListener('pointerdown', this.onPointerDown)
            svg.removeEventListener('pointermove', this.onPointerMove)
            svg.removeEventListener('pointerup', this.onPointerUp)
            svg.removeEventListener('pointercancel', this.onPointerUp)
            svg.removeEventListener('wheel', this.onWheel)
        }
        window.removeEventListener('keydown', this.onKeyDown)
        window.removeEventListener('keyup', this.onKeyUp)
        this.svg = null
    }

    /* ----------------------------------------------------------------- state */

    getDoc(): SceneDoc {
        return this.doc
    }

    isDirty(): boolean {
        return this.dirty
    }

    get canUndo(): boolean {
        return this.history.canUndo
    }

    get canRedo(): boolean {
        return this.history.canRedo
    }

    /** Replaces the document (initial load, or a reload after save). */
    setDoc(doc: SceneDoc, { keepHistory = false } = {}): void {
        this.doc = doc
        if (!keepHistory) this.history.reset()
        this.dirty = false
        this.selectedKey = null
        this.selectedVertex = null
        this.selectedHole = null
        this.drawing = null
        this.ghost = null
        this.issues = validate(doc)
        this.render()
        this.hooks.onChange(false)
        this.hooks.onSelectionChange(null)
    }

    markSaved(): void {
        this.dirty = false
        this.hooks.onChange(false)
    }

    setTool(tool: ToolId): void {
        if (this.hooks.toolAvailable && !this.hooks.toolAvailable(tool)) return
        const leavingCalibrate = this.tool === 'calibrate' && tool !== 'calibrate'
        const changed = this.tool !== tool
        this.tool = tool
        this.drawing = null
        this.ghost = null
        this.ghostWall = null
        if (leavingCalibrate) this.finishCalibration()
        // Arming a creation tool drops the selection: the right panel is about
        // to describe *what will be drawn*, and an element left selected there
        // would keep showing what was drawn last instead.
        //
        // The hand drops it for the opposite reason — nothing is armed, and
        // nothing should be editable. Panning *with* a selection is still one
        // key away: holding space pans in any tool, which is the gesture for
        // "move the view, keep my place".
        if (changed && (CREATION_TOOLS.includes(tool) || tool === 'pan')) this.select(null)
        if (tool === 'calibrate') {
            // A layer picked in the panel is the obvious thing to calibrate;
            // otherwise keep the current target, or take the first layer.
            const preferred = this.selectedLayerUid
                ?? this.calibrationUid
                ?? this.sceneLayerList()[0]?.registrationUid
                ?? null
            this.select(null)
            if (preferred !== this.calibrationUid) this.setCalibrationTarget(preferred)
            else this.emitCalibration()
        }
        this.render()
        if (changed) this.hooks.onToolChange?.(tool)
    }

    setLayer(id: LayerId, patch: Partial<{ on: boolean; opacity: number }>): void {
        this.layers[id] = { ...this.layers[id], ...patch }
        this.render()
    }

    setUnderlay(key: string, patch: Partial<{ visible: boolean; opacity: number }>): void {
        const u = this.doc.underlays.find((x) => x.key === key)
        if (!u) return
        Object.assign(u, patch)
        this.render()
    }

    setSnap(on: boolean): void {
        this.snapEnabled = on
    }

    /**
     * Show or hide the grouping overlay: a per-grouper tint on member spaces
     * plus the grouper's name at their centroid (spec 2026-08-30-01 §8).
     * Nothing about it is stored — it is derived from the `group_member` edges the
     * document already carries, so it is a display switch and not an edit.
     */
    setShowGroupers(on: boolean): void {
        this.showGroupers = on
        this.render()
    }

    /**
     * Hide the runs of these networks (the layer panel's per-network and
     * per-family switches). A hidden run stays in the document and is saved;
     * it is only not drawn, so it cannot be clicked either — a selection on
     * one is dropped rather than left editing something invisible.
     */
    setHiddenNetworks(codes: Iterable<string>): void {
        this.hiddenNetworks = new Set(codes)
        const hidden = (key: string | null): boolean => {
            const el = elementByKey(this.doc, key)
            return (el?.kind === 'run' || el?.kind === 'riser') && this.hiddenNetworks.has(el.network)
        }
        // Companions too: a group edit must never reach a run nobody can see.
        const visible = this.selectedKeys().filter((k) => !hidden(k))
        if (visible.length !== this.selectedKeys().length) {
            const [primary = null, ...rest] = visible
            this.select(primary)
            if (rest.length) {
                this.extraKeys = rest
                this.hooks.onSelectionChange(primary, rest)
            }
        }
        this.render()
    }

    /**
     * File every selected space under `ref`, or clear the grouping with `null`
     * — one undo step for the whole selection (spec 2026-08-30-01 §5).
     * Non-space elements are left alone; `compatible` already makes a mixed
     * selection impossible, and this keeps it true if that ever changes.
     */
    assignGrouper(ref: GrouperRef | null): void {
        this.updateAllSelected((el) => {
            if (el.kind !== 'space') return
            if (ref) el.owner.grouper = { ...ref }
            else delete el.owner.grouper
        })
    }

    /** Slide the view by a screen delta — the arrow keys with nothing selected. */
    panBy(dxPx: number, dyPx: number): void {
        this.view = { ...this.view, x: this.view.x + dxPx, y: this.view.y + dyPx }
        this.render()
    }

    /**
     * Move the selection by a number of screen pixels. Snapping stays out of it: the
     * point of a nudge is the exact offset asked for, and a magnet that pulls
     * it somewhere else would make the keyboard useless for fine placement.
     */
    nudgeSelected(dx: number, dy: number, stepPx: number): void {
        if (this.exploring) return
        const keys = this.selectedKeys()
        if (!keys.length || (dx === 0 && dy === 0)) return
        const step = stepPx / Math.max(this.view.k, 1e-9)
        const moved = { x: dx * step, y: dy * step }
        this.mutate(() => {
            for (const key of keys) this.translate(key, moved)
        })
        const el = elementByKey(this.doc, this.selectedKey)
        if (el?.kind === 'equipment') this.hooks.onEquipmentMoved?.(el.key)
    }

    /** Slide one element by a delta, whatever it is made of. */
    private translate(key: string, moved: Point): void {
        {
            const el = elementByKey(this.doc, key)
            if (!el) return
            switch (el.kind) {
                case 'wall':
                    el.start = { x: el.start.x + moved.x, y: el.start.y + moved.y }
                    el.end = { x: el.end.x + moved.x, y: el.end.y + moved.y }
                    break
                case 'space':
                    el.boundary = el.boundary.map((p) => ({ x: p.x + moved.x, y: p.y + moved.y }))
                    if (el.holes) el.holes = el.holes.map((h) => h.map((p) => ({ x: p.x + moved.x, y: p.y + moved.y })))
                    break
                case 'opening':
                    el.center = { x: el.center.x + moved.x, y: el.center.y + moved.y }
                    break
                case 'equipment':
                    el.position = { x: el.position.x + moved.x, y: el.position.y + moved.y }
                    break
                case 'run':
                    el.points = el.points.map((p) => ({ x: p.x + moved.x, y: p.y + moved.y }))
                    break
                case 'riser':
                    el.position = { x: el.position.x + moved.x, y: el.position.y + moved.y }
                    break
            }
        }
    }

    /** What an uncalibrated frame needs to size a millimetre habit. */
    /**
     * The drawing's own points — equipment left out, so a footprint is never
     * measured against itself — or everything when equipment is all there is.
     */
    private drawingBox(): ReturnType<typeof bboxOfPoints> {
        const pts: Point[] = []
        for (const el of this.doc.elements) if (el.kind !== 'equipment') pts.push(...elementPoints(el))
        for (const u of this.doc.underlays) {
            pts.push(applyTransform(u.transform, { x: 0, y: 0 }))
            pts.push(applyTransform(u.transform, { x: 1, y: u.height }))
        }
        return bboxOfPoints(pts.length ? pts : documentPoints(this.doc))
    }

    private lengthContext(): LengthContext {
        return {
            k: this.view.k,
            extent: () => {
                const bb = this.drawingBox()
                return bb ? Math.max(bb.x1 - bb.x0, bb.y1 - bb.y0) : 0
            },
        }
    }

    /**
     * A millimetre footprint as plan units, capped at a quarter of the
     * drawing's smaller side. A cap that bites tells the shell, once per call.
     */
    footprintToUnits(widthMm: number, depthMm: number): { width: number; depth: number } {
        const width = this.mmToUnits(widthMm)
        const depth = this.mmToUnits(depthMm)
        const fit = clampFootprint(width, depth, this.drawingSide())
        if (fit.clamped) this.hooks.onFootprintReduced?.()
        return { width: fit.width, depth: fit.depth }
    }

    /** The drawing's smaller side in plan units (0 when there is no drawing). */
    private drawingSide(): number {
        const bb = this.drawingBox()
        let side = bb ? Math.min(bb.x1 - bb.x0, bb.y1 - bb.y0) : 0
        if (bb && !(side > 0)) side = Math.max(bb.x1 - bb.x0, bb.y1 - bb.y0)
        return side
    }

    /**
     * A millimetre length (wall thickness, opening width) as plan units, capped
     * at `fraction` of the drawing's smaller side so a badly scaled frame never
     * yields a wall or opening larger than the drawing can hold.
     */
    lengthToUnits(mm: number, fraction: number): number {
        return clampFootprint(this.mmToUnits(mm), 0, this.drawingSide(), fraction).width
    }

    /** Plan units per millimetre on this frame, at the current zoom. */
    unitsPerMm(): number {
        return calUnitsPerMm(this.doc.calibration, this.lengthContext())
    }

    /** A millimetre habit as plan units. */
    mmToUnits(mm: number): number {
        return calMmToUnits(mm, this.doc.calibration, this.lengthContext())
    }

    /** A plan-unit length as millimetres, for display and for remembering a size. */
    unitsToMm(units: number): number {
        return calUnitsToMm(units, this.doc.calibration, this.lengthContext())
    }

    /**
     * The thickness the next wall is drawn with. Sticky, like the exp editor's
     * `lastThickness`: the shell arms it before drawing and re-arms it whenever
     * a wall's thickness is edited, so a plan traced at 200 mm keeps drawing at
     * 200 mm.
     */
    setWallThickness(mm: number): void {
        this.lastThickness = mm
    }

    /** Every selected element, primary first. */
    selectedKeys(): string[] {
        return this.selectedKey ? [this.selectedKey, ...this.extraKeys] : []
    }

    /**
     * Can these two be worked on together? One **category** at a time
     * (spec 2026-09-06-01 §1) — the layer panel's own groups: Unit, Space,
     * Walls, Equipments, and openings split into doors and windows.
     *
     * The rule used to be finer than that: equipment had to share a `typeCode`
     * and openings an asset type, so shift-clicking a radiator then a boiler
     * did nothing at all. What a group edit actually needs is that the fields
     * *it writes* mean the same thing for every member, and the ones that do
     * not — an equipment's type, its per-type size presets, a pending
     * opening's type — are edited on the primary alone by the inspector.
     */
    private compatible(a: EditorElement, b: EditorElement): boolean {
        return sameCategory(a, b)
    }

    /**
     * Shift-click: add to, or remove from, the selection. An element of another
     * category is ignored rather than replacing the group — silently dropping
     * four selected radiators because the fifth click landed on a wall is worse
     * than doing nothing.
     */
    toggleSelect(key: string): void {
        const el = elementByKey(this.doc, key)
        if (!el) return
        if (!this.selectedKey) {
            this.select(key)
            return
        }
        if (key === this.selectedKey) {
            // Dropping the primary promotes the next companion, if any.
            const [next, ...rest] = this.extraKeys
            this.selectedKey = next ?? null
            this.extraKeys = next ? rest : []
            this.selectedVertex = null
            this.selectedHole = null
            this.render()
            this.hooks.onSelectionChange(this.selectedKey, this.extraKeys)
            return
        }
        if (this.extraKeys.includes(key)) {
            this.extraKeys = this.extraKeys.filter((k) => k !== key)
            this.render()
            this.hooks.onSelectionChange(this.selectedKey, this.extraKeys)
            return
        }
        const primary = elementByKey(this.doc, this.selectedKey)
        if (!primary || !this.compatible(primary, el)) return
        this.extraKeys = [...this.extraKeys, key]
        this.render()
        this.hooks.onSelectionChange(this.selectedKey, this.extraKeys)
    }

    /**
     * A rubber band, released (spec 2026-09-06-01 §4). Selection only: nothing
     * about the document changes, so there is no history entry to push.
     *
     * The rectangle is read in **world** millimetres — the view has no
     * rotation, so an axis-aligned screen box is an axis-aligned world box —
     * and an element counts only when its whole geometry is inside it.
     */
    private applyMarquee(a: Point, b: Point): void {
        const p0 = this.scrToMm(a)
        const p1 = this.scrToMm(b)
        const box: BBox = {
            x0: Math.min(p0.x, p1.x),
            y0: Math.min(p0.y, p1.y),
            x1: Math.max(p0.x, p1.x),
            y1: Math.max(p0.y, p1.y),
        }
        // A layer nobody can see is a layer nobody meant to select: hiding the
        // equipment is exactly how a user clears the way for a box over the
        // rooms underneath it.
        const candidates = this.doc.elements.filter((el) =>
            this.layers[categoryLayer(selectionCategory(el))].on
            && elementInsideBox(el, box, this.doc.symbols))
        if (!candidates.length) {
            this.render()
            return
        }

        const primary = elementByKey(this.doc, this.selectedKey)
        if (primary) {
            // Adding to a group never changes what that group *is*: only the
            // current category joins, and the primary keeps the handles.
            const category = selectionCategory(primary)
            const held = new Set(this.selectedKeys())
            const added = candidates
                .filter((el) => selectionCategory(el) === category && !held.has(el.key))
                .map((el) => el.key)
            if (!added.length) {
                this.render()
                return
            }
            this.extraKeys = [...this.extraKeys, ...added]
        } else {
            // Nothing selected: the box picks the category it caught most of,
            // ties broken in layer-panel order. A group the inspector cannot
            // edit is never what the gesture meant.
            const category = dominantCategory(candidates)
            if (!category) {
                this.render()
                return
            }
            const picked = candidates.filter((el) => selectionCategory(el) === category)
            this.selectedKey = picked[0].key
            this.extraKeys = picked.slice(1).map((el) => el.key)
            this.selectedVertex = null
            this.selectedHole = null
            this.blinkKey = null
            if (this.selectedLayerUid) {
                this.selectedLayerUid = null
                this.hooks.onLayerSelectionChange?.(null)
            }
        }
        this.render()
        // Once, for the whole band — a shell that re-rendered per element would
        // flicker its way through a hundred rooms.
        this.hooks.onSelectionChange(this.selectedKey, this.extraKeys)
    }

    /** Apply one patch to every selected element, as a single undo step. */
    updateAllSelected(patch: (el: EditorElement) => void): void {
        const keys = this.selectedKeys()
        if (!keys.length) return
        this.mutate(() => {
            for (const key of keys) {
                const el = elementByKey(this.doc, key)
                if (el) patch(el)
            }
        })
    }

    /**
     * `blinkParent` is what separates a click on the drawing from every other
     * way a selection can arrive; only the pointer path passes it, and only for
     * the kinds whose parent is worth pointing at (a room's unit, an
     * equipment's room).
     */
    select(key: string | null, vertex: number | null = null, blinkParent = false): void {
        this.selectedKey = key
        this.extraKeys = []
        this.selectedVertex = vertex
        this.selectedHole = null
        this.blinkKey = blinkParent && key && this.blinkable(key) ? key : null
        if (key && this.selectedLayerUid) {
            this.selectedLayerUid = null
            this.hooks.onLayerSelectionChange?.(null)
        }
        this.render()
        this.hooks.onSelectionChange(key)
    }

    /**
     * Whether clicking this element should point at its parent. Only a room and
     * an equipment ask a question the drawing can answer — a unit is filed
     * under the floor, which is the whole plan, and an opening under the wall
     * it was cut into, which is already under the pointer.
     */
    private blinkable(key: string): boolean {
        const el = elementByKey(this.doc, key)
        if (!el) return false
        return el.kind === 'equipment' || (el.kind === 'space' && !isGrouperElement(el))
    }

    /**
     * Selects a whole layer for direct manipulation (§6). Element selection and
     * layer selection are exclusive: the handles on screen must never be
     * ambiguous.
     */
    selectLayer(registrationUid: string | null): void {
        this.selectedLayerUid = registrationUid
        if (registrationUid) {
            this.selectedKey = null
            this.selectedVertex = null
            this.selectedHole = null
            this.hooks.onSelectionChange(null)
        }
        this.render()
        this.hooks.onLayerSelectionChange?.(registrationUid)
    }

    /** The scene's calibratable layers, freshest first read from the document. */
    sceneLayerList(): SceneLayer[] {
        return sceneLayers(this.doc)
    }

    /** Runs a mutation inside one undo step. */
    mutate(fn: () => void): void {
        const before = this.snapshot()
        fn()
        this.history.push(before)
        this.commit()
    }

    private snapshot(): string {
        return JSON.stringify(this.doc)
    }

    private commit(): void {
        // An edit is not a click: whatever the blink was answering, the user has
        // moved on to changing the thing they selected.
        this.blinkKey = null
        this.dirty = true
        this.issues = validate(this.doc)
        this.render()
        this.hooks.onChange(true)
    }

    undo(): void {
        const restored = this.history.undo(this.snapshot())
        if (restored === null) return
        this.restore(restored)
    }

    redo(): void {
        const restored = this.history.redo(this.snapshot())
        if (restored === null) return
        this.restore(restored)
    }

    private restore(snapshot: string): void {
        this.doc = JSON.parse(snapshot) as SceneDoc
        if (this.selectedKey && !elementByKey(this.doc, this.selectedKey)) {
            this.selectedKey = null
            this.selectedVertex = null
            this.selectedHole = null
            this.hooks.onSelectionChange(null)
        }
        this.commit()
    }

    /* ------------------------------------------------------------ conversion */

    private mmToScr = (p: Point): Point => ({ x: p.x * this.view.k + this.view.x, y: p.y * this.view.k + this.view.y })
    private scrToMm = (p: Point): Point => ({ x: (p.x - this.view.x) / this.view.k, y: (p.y - this.view.y) / this.view.k })

    private eventScr(e: { clientX: number; clientY: number }): Point {
        const r = this.svg?.getBoundingClientRect()
        return { x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) }
    }

    private snap(raw: Point, anchor?: Point, exclude?: { key: string; vi?: number }) {
        return snapPoint(raw, this.doc, {
            scale: this.view.k,
            enabled: this.snapEnabled && !this.altDown,
            axisLock: this.shiftDown,
            anchor,
            exclude,
        })
    }

    /* -------------------------------------------------------------- rendering */

    render(): void {
        if (!this.world) return
        this.world.setAttribute('transform', `translate(${this.view.x},${this.view.y}) scale(${this.view.k})`)
        // Every view change lands here — pan, zoom, wheel, fit, the arrow keys —
        // so this is the one place the shell has to listen to. Guarded because
        // `render` is also every *document* change, and the viewport has not
        // moved when a wall does.
        const stamp = `${this.view.x},${this.view.y},${this.view.k}`
        if (stamp !== this.lastViewStamp) {
            this.lastViewStamp = stamp
            this.hooks.onViewChange?.(this.viewport())
        }
        const markup = renderWorld(this.doc, {
            layers: this.layers,
            scale: this.view.k,
            underlayHref: this.hooks.underlayHref,
            selectedKey: this.selectedKey,
            selectedKeys: this.selectedKeys(),
            hoverKey: this.hoverKey,
            ambiguousKeys: this.ambiguousKeys,
            parentSpaceKey: parentSpaceKeyOf(this.doc, elementByKey(this.doc, this.blinkKey) ?? null),
            showGroupers: this.showGroupers,
            hiddenNetworks: this.hiddenNetworks,
            ...(this.hooks.typeLabelOf ? { typeLabelOf: this.hooks.typeLabelOf } : {}),
            ...(this.hooks.networkLabelOf ? { networkLabelOf: this.hooks.networkLabelOf } : {}),
        })
        for (const id of LAYER_IDS) {
            const node = this.layerNodes.get(id)
            if (node && node.innerHTML !== markup[id]) node.innerHTML = markup[id]
        }
        if (this.hud) this.hud.innerHTML = this.renderHud()
    }

    private renderHud(): string {
        let h = ''
        const S = this.mmToScr

        const d = this.drawing
        if (d?.kind === 'wall' && d.last && d.cur) {
            const a = S(d.last)
            const b = S(d.cur)
            h += `<line class="plan-rubber" x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}"/>`
        }
        if ((d?.kind === 'room' || d?.kind === 'hole') && d.pts?.length) {
            const scr = d.pts.map(S)
            const cur = d.cur ? S(d.cur) : null
            const pts = scr.map((p) => `${p.x},${p.y}`).join(' ') + (cur ? ` ${cur.x},${cur.y}` : '')
            h += `<polyline class="${d.kind === 'hole' ? 'plan-rubber-hole' : 'plan-rubber-fill'}" points="${pts}"/>`
            h += `<circle class="plan-first-point" cx="${scr[0].x}" cy="${scr[0].y}" r="6"/>`
        }
        if (d?.kind === 'run' && d.pts?.length) {
            // An open line, never a filled ring, in the armed network's colour.
            const scr = d.pts.map(S)
            const cur = d.cur ? S(d.cur) : null
            const pts = scr.map((p) => `${p.x},${p.y}`).join(' ') + (cur ? ` ${cur.x},${cur.y}` : '')
            const code = this.hooks.runDefaults?.().network
            const color = (code && this.doc.networks?.find((n) => n.code === code)?.color) || 'var(--primary)'
            h += `<polyline class="plan-rubber-run" points="${pts}" style="stroke:${esc(color)}"/>`
            for (const p of scr) h += `<circle class="plan-run-vertex" cx="${p.x}" cy="${p.y}" r="3"/>`
        }
        if (this.marquee) {
            const { a, b } = this.marquee
            h += `<rect class="plan-marquee" x="${Math.min(a.x, b.x)}" y="${Math.min(a.y, b.y)}"`
                + ` width="${Math.abs(b.x - a.x)}" height="${Math.abs(b.y - a.y)}"/>`
        }
        if (this.ghost) h += this.ghost
        if (this.snapIndicator) {
            const p = S(this.snapIndicator)
            h += `<circle class="plan-snap-dot" cx="${p.x}" cy="${p.y}" r="7"/>`
        }

        // The segment an auto-calibration measured, flashed over the plan so the
        // number in the summary strip is answerable by looking (spec
        // 2026-09-05-07 §4). It is HUD-only and self-clearing: nothing about the
        // document changes, so it can never be saved or undone.
        if (this.flashSegment) {
            const a = S(this.flashSegment.a)
            const b = S(this.flashSegment.b)
            h += `<line class="plan-flash-segment" x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}"/>`
                + `<circle class="plan-flash-end" cx="${a.x}" cy="${a.y}" r="5"/>`
                + `<circle class="plan-flash-end" cx="${b.x}" cy="${b.y}" r="5"/>`
        }

        // A calibration the job only proposed: the circle it measured, whole,
        // so "is that the turning circle?" is answered at a glance.
        if (this.proposalSegment) {
            const a = S(this.proposalSegment.a)
            const b = S(this.proposalSegment.b)
            const cx = (a.x + b.x) / 2
            const cy = (a.y + b.y) / 2
            const r = Math.hypot(b.x - a.x, b.y - a.y) / 2
            h += `<circle class="plan-proposal-circle" data-testid="plan-editor-calibration-proposal-circle" cx="${cx}" cy="${cy}" r="${r}"/>`
                + `<line class="plan-proposal-diameter" x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}"/>`
        }

        if (this.tool === 'calibrate') h += this.hudForCalibration()
        if (this.selectedLayerUid) h += this.hudForLayer(this.selectedLayerUid)

        // Companions get an outline and nothing else: the handles belong to the
        // one element an edit is anchored on, and four sets of them would be
        // four ways to grab the wrong thing.
        for (const key of this.extraKeys) {
            const companion = elementByKey(this.doc, key)
            if (companion) h += this.hudOutline(companion)
        }
        const el = elementByKey(this.doc, this.selectedKey)
        if (el) h += this.hudForSelection(el)
        return h
    }

    /** The bare outline of an element, with no handles on it. */
    private hudOutline(el: EditorElement): string {
        const S = this.mmToScr
        if (el.kind === 'wall') {
            const a = S(el.start)
            const b = S(el.end)
            return `<line class="plan-sel-line companion" x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}"/>`
        }
        if (el.kind === 'equipment') {
            const symbol = el.typeCode ? this.doc.symbols[el.typeCode] : undefined
            const cs = equipmentFootprintCorners(el, symbol).map(S)
            return `<polygon class="plan-sel-line companion" points="${cs.map((p) => `${p.x},${p.y}`).join(' ')}"/>`
        }
        if (el.kind === 'opening') {
            const rad = (el.directionDeg * Math.PI) / 180
            const hw = el.width / 2
            const a = S({ x: el.center.x - Math.cos(rad) * hw, y: el.center.y - Math.sin(rad) * hw })
            const b = S({ x: el.center.x + Math.cos(rad) * hw, y: el.center.y + Math.sin(rad) * hw })
            return `<line class="plan-sel-line companion" x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}"/>`
        }
        if (el.kind === 'run') {
            return `<polyline class="plan-sel-line companion" fill="none" points="${el.points.map(S).map((p) => `${p.x},${p.y}`).join(' ')}"/>`
        }
        if (el.kind === 'riser') {
            const c = S(el.position)
            return `<circle class="plan-sel-line companion" fill="none" cx="${c.x}" cy="${c.y}" r="12"/>`
        }
        return ''
    }

    /** Anchor pins, the pending pair and the measure segment (§1, §2). */
    private hudForCalibration(): string {
        const S = this.mmToScr
        const uid = this.calibrationUid
        if (!uid) return ''
        const current = layerTransform(this.doc, uid)
        let h = this.hudForCommittedCalibration()
        if (current) {
            this.pairs.forEach((pair, i) => {
                const from = S(applyTransform(current, pair.source))
                const to = S(pair.target)
                h += `<line class="plan-pair-link" x1="${from.x}" y1="${from.y}" x2="${to.x}" y2="${to.y}"/>` +
                    `<circle class="plan-pair-pin" data-pair="${i}" cx="${to.x}" cy="${to.y}" r="7"/>` +
                    `<text class="plan-pair-label" x="${to.x}" y="${to.y + 4}" text-anchor="middle">${i + 1}</text>`
            })
        }
        if (this.pendingPair) {
            const p = S(this.pendingPair.frame)
            h += `<circle class="plan-pair-pending" cx="${p.x}" cy="${p.y}" r="7"/>`
        }
        if (this.measure?.p1) {
            const a = S(this.measure.p1)
            h += `<circle class="plan-measure-dot" cx="${a.x}" cy="${a.y}" r="5"/>`
            if (this.measure.cur) {
                const b = S(this.measure.cur)
                h += `<line class="plan-measure-line" x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}"/>`
            }
        }
        return h
    }

    /**
     * What the frame is calibrated to, drawn on the stage (spec 2026-09-06-01
     * §3). Only in **calibration** mode: on any other layer the stored pairs
     * are transform provenance, not a scale, and drawing them here would say
     * something untrue about the plan.
     *
     * The style is deliberately not the pending work's, and not the
     * auto-calibration flash's either: dashed and muted, so "this is what is
     * already set" never reads as "this is what you are about to set".
     */
    private hudForCommittedCalibration(): string {
        if (!this.showCurrentCalibration) return ''
        if (this.calibrationMode !== 'calibration') return ''
        const cal = this.currentCalibration
        if (!cal) return ''
        const S = this.mmToScr
        // A measure in progress dims it further: the point of keeping it on
        // screen is comparing the old vector with the new one being drawn.
        const dim = this.measure ? ' dim' : ''

        if ((cal.method === 'measure' || cal.method === 'auto') && cal.measure) {
            const a = S(cal.measure.a)
            const b = S(cal.measure.b)
            const label = `${formatDistanceMm(cal.measure.distance_mm)} · ${cal.mm_per_unit.toFixed(4)} mm/u`
            return `<line class="plan-cal-current-line${dim}" x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}"/>`
                + this.arrowHead(a, b, dim) + this.arrowHead(b, a, dim)
                + `<circle class="plan-cal-current-pin${dim}" cx="${a.x}" cy="${a.y}" r="4"/>`
                + `<circle class="plan-cal-current-pin${dim}" cx="${b.x}" cy="${b.y}" r="4"/>`
                + `<text class="plan-cal-current-label${dim}" x="${(a.x + b.x) / 2}" y="${(a.y + b.y) / 2 - 8}"`
                + ` text-anchor="middle">${esc(label)}</text>`
        }

        if (cal.method === 'anchor_pairs' && cal.pairs?.length) {
            let h = ''
            cal.pairs.forEach((pair, i) => {
                const target = pair.target
                if (!target) return
                const to = S(target)
                if (pair.source) {
                    const from = S(pair.source)
                    h += `<line class="plan-cal-current-line${dim}" x1="${from.x}" y1="${from.y}" x2="${to.x}" y2="${to.y}"/>`
                }
                h += `<circle class="plan-cal-current-pin${dim}" cx="${to.x}" cy="${to.y}" r="7"/>`
                    + `<text class="plan-cal-current-label${dim}" x="${to.x}" y="${to.y + 4}"`
                    + ` text-anchor="middle">${i + 1}</text>`
            })
            return h
        }
        // `manual`, or a calibration with no audit trail: there is nothing
        // honest to draw, and the panel states the ratio on its own.
        return ''
    }

    /**
     * A small V pointing at `end`, coming from `from` — one head of the scale
     * vector. Its point is inset by the end pin's radius so the head sits
     * beside the pin instead of disappearing under it.
     */
    private arrowHead(end: Point, from: Point, dim: string): string {
        const ang = Math.atan2(end.y - from.y, end.x - from.x)
        const inset = 5
        const len = 10
        const spread = 0.42
        const tip: Point = { x: end.x - Math.cos(ang) * inset, y: end.y - Math.sin(ang) * inset }
        const p = (sign: number): Point => ({
            x: tip.x - Math.cos(ang + sign * spread) * len,
            y: tip.y - Math.sin(ang + sign * spread) * len,
        })
        const l = p(1)
        const r = p(-1)
        return `<polyline class="plan-cal-current-arrow${dim}" points="${l.x},${l.y} ${tip.x},${tip.y} ${r.x},${r.y}"/>`
    }

    /** The selected layer's bounding box, with its move and rotate handles (§6). */
    private hudForLayer(registrationUid: string): string {
        const bb = layerBBox(this.doc, registrationUid)
        if (!bb) return ''
        const S = this.mmToScr
        const a = S({ x: bb.x0, y: bb.y0 })
        const b = S({ x: bb.x1, y: bb.y1 })
        const cx = (a.x + b.x) / 2
        const cy = (a.y + b.y) / 2
        return `<rect class="plan-layer-box" x="${Math.min(a.x, b.x)}" y="${Math.min(a.y, b.y)}"` +
            ` width="${Math.abs(b.x - a.x)}" height="${Math.abs(b.y - a.y)}"/>` +
            `<line class="plan-rot-arm" x1="${cx}" y1="${cy}" x2="${cx}" y2="${Math.min(a.y, b.y) - 28}"/>` +
            `<circle data-handle="lrot" class="plan-handle rot" cx="${cx}" cy="${Math.min(a.y, b.y) - 28}" r="6"/>` +
            `<rect data-handle="lmove" class="plan-handle layer-move" x="${cx - 7}" y="${cy - 7}" width="14" height="14"/>`
    }

    private hudForSelection(el: EditorElement): string {
        const S = this.mmToScr
        const handle = (p: Point, name: string, active = false): string =>
            `<rect data-handle="${esc(name)}" class="plan-handle${active ? ' active' : ''}" x="${p.x - 4.5}" y="${p.y - 4.5}" width="9" height="9"/>`
        const midHandle = (p: Point, name: string): string =>
            `<circle data-handle="${esc(name)}" class="plan-handle mid" cx="${p.x}" cy="${p.y}" r="4"/>`

        if (el.kind === 'wall') {
            const a = S(el.start)
            const b = S(el.end)
            return `<line class="plan-sel-line" x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}"/>` +
                handle(a, 'w0') + handle(b, 'w1')
        }
        if (el.kind === 'space') {
            const pts = el.boundary.map(S)
            if (!pts.length) return ''
            // Holes get the boundary's handles, under their own names — a
            // pillar's corner is moved, inserted and deleted the same way.
            const holes = spaceHoles(el).map((ring) => ring.map(S))
            let h = holes.length
                ? `<path class="plan-sel-fill" fill-rule="evenodd" d="${[pts, ...holes].map(ringPath).join(' ')}"/>`
                : `<polygon class="plan-sel-fill" points="${pts.map((p) => `${p.x},${p.y}`).join(' ')}"/>`
            pts.forEach((p, i) => {
                h += handle(p, `v${i}`, this.selectedHole === null && this.selectedVertex === i)
                const q = pts[(i + 1) % pts.length]
                h += midHandle({ x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 }, `m${i}`)
            })
            holes.forEach((ring, hi) => ring.forEach((p, i) => {
                h += handle(p, `h${hi}v${i}`, this.selectedHole === hi && this.selectedVertex === i)
                const q = ring[(i + 1) % ring.length]
                h += midHandle({ x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 }, `h${hi}m${i}`)
            }))
            return h
        }
        if (el.kind === 'opening') {
            const rad = (el.directionDeg * Math.PI) / 180
            const ux = Math.cos(rad)
            const uy = Math.sin(rad)
            const hw = el.width / 2
            const e0 = S({ x: el.center.x - ux * hw, y: el.center.y - uy * hw })
            const e1 = S({ x: el.center.x + ux * hw, y: el.center.y + uy * hw })
            const c = S(el.center)
            return `<line class="plan-sel-line" x1="${e0.x}" y1="${e0.y}" x2="${e1.x}" y2="${e1.y}"/>` +
                `<circle class="plan-handle" data-handle="oc" cx="${c.x}" cy="${c.y}" r="5"/>` +
                handle(e0, 'ow0') + handle(e1, 'ow1')
        }
        if (el.kind === 'riser') {
            // A point: an outline ring; the riser itself is what drags.
            const c = S(el.position)
            return `<circle class="plan-sel-line" fill="none" cx="${c.x}" cy="${c.y}" r="12"/>`
        }
        if (el.kind === 'run') {
            // An open polyline: a handle per vertex, and a mid-handle per
            // segment that inserts one — the room gesture without the closing
            // edge.
            const pts = el.points.map(S)
            let h = `<polyline class="plan-sel-line" fill="none" points="${pts.map((p) => `${p.x},${p.y}`).join(' ')}"/>`
            pts.forEach((p, i) => {
                h += handle(p, `r${i}`, this.selectedVertex === i)
                const q = pts[i + 1]
                if (q) h += midHandle({ x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 }, `rm${i}`)
            })
            return h
        }
        // equipment: footprint outline + anchor handle + rotation handle.
        //
        // The outline is the *symbol's* footprint, which an off-centre anchor
        // moves away from the position — see `equipmentFootprintOffset`. The
        // move handle stays on the position: that is the point the symbol is
        // pinned by, the point that snaps, and the point rotation turns around.
        const rad = (el.rotationDeg * Math.PI) / 180
        const cos = Math.cos(rad)
        const sin = Math.sin(rad)
        const symbol = el.typeCode ? this.doc.symbols[el.typeCode] : undefined
        const cs = equipmentFootprintCorners(el, symbol).map(S)
        const off = equipmentFootprintOffset(symbol, el.width, el.depth)
        const centre = S(el.position)
        const armLen = Math.max(el.depth, el.width) / 2 + 24 / Math.max(this.view.k, 1e-9)
        const arm = S({
            x: el.position.x + off.x * cos - (off.y + armLen) * sin,
            y: el.position.y + off.x * sin + (off.y + armLen) * cos,
        })
        // The arm grows out of the footprint's own middle, so it reads as the
        // shape's handle rather than a line leaving the anchor sideways.
        const armRoot = S({
            x: el.position.x + off.x * cos - off.y * sin,
            y: el.position.y + off.x * sin + off.y * cos,
        })
        return `<polygon class="plan-sel-line" points="${cs.map((p) => `${p.x},${p.y}`).join(' ')}"/>` +
            `<line class="plan-rot-arm" x1="${armRoot.x}" y1="${armRoot.y}" x2="${arm.x}" y2="${arm.y}"/>` +
            `<circle data-handle="erot" class="plan-handle rot" cx="${arm.x}" cy="${arm.y}" r="5"/>` +
            handle(centre, 'ec')
    }

    /* --------------------------------------------------------------- pointers */

    private onPointerDown = (e: PointerEvent): void => {
        const target = e.target as Element
        if (e.button === 1 || this.spaceDown || this.tool === 'pan') {
            this.startDrag(e, { type: 'view', vx: this.view.x, vy: this.view.y })
            return
        }
        if (e.button !== 0) return
        const scr = this.eventScr(e)
        const now = pressedAt(e)
        const previous = this.lastClick
        this.lastClick = { at: now, scr }
        // A double click is only *armed* here and acted on at release, when it
        // is known that no drag happened. Acting now would eat the second click
        // of "click to select, then drag straight away" — a normal gesture that
        // is indistinguishable from a double click until the pointer moves.
        // Shift is building a selection, which is never a double click.
        this.pendingDouble = !e.shiftKey
            && !!previous
            && now - previous.at <= DOUBLE_CLICK_MS
            && Math.hypot(previous.scr.x - scr.x, previous.scr.y - scr.y) <= DOUBLE_CLICK_PX
            ? { scr, target: e.target }
            : null
        if (this.pendingDouble) this.lastClick = null
        const handleTarget = target.closest?.('[data-handle]') as HTMLElement | null
        const elementTarget = target.closest?.('[data-key]') as HTMLElement | null

        if (this.tool === 'calibrate') {
            this.calibrateClick(scr)
            return
        }

        if (this.tool === 'select') {
            // Selecting the smallest outline draws its handles, and they land
            // on the very pixels the next click needs — so without this, one
            // click into an overlap is as far as anyone ever gets. A *repeat*
            // click at the same spot with a walk in progress continues the walk
            // (spec 2026-08-30-03 §7); the first click still selects a vertex,
            // and dragging a handle is unaffected because a drag moves.
            if (handleTarget && !e.shiftKey && this.ambiguousKeys.length > 1 && this.atAmbiguitySpot(scr)) {
                const next = this.pickGrouperAt(scr)
                if (next) {
                    this.select(next)
                    this.startDrag(e, { type: 'view', vx: this.view.x, vy: this.view.y })
                    return
                }
            }
            if (handleTarget) {
                const handle = handleTarget.dataset.handle as string
                // A layer's own handles move the whole registration (§6).
                if (handle === 'lmove' || handle === 'lrot') {
                    this.startLayerDrag(e, handle)
                    return
                }
                // Alt on the equipment's own move handle resizes instead of
                // moving. The handle sits on the anchor, which is exactly the
                // point a resize measures from — and once an equipment is
                // selected that handle covers the shape, so without this the
                // Alt-drag below would be unreachable on the second gesture.
                this.startHandleDrag(e, e.altKey && handle === 'ec' ? 'esize' : handle)
                return
            }
            const domKey = elementTarget && elementTarget.dataset.kind !== 'underlay'
                ? elementTarget.dataset.key as string
                : undefined
            const domEl = domKey ? elementByKey(this.doc, domKey) : undefined
            // A click that lands on a **grouper** — its edge or its interior —
            // goes through the walk rather than straight to the DOM's answer
            // (spec 2026-08-30-03 §7). Otherwise the outline drawn last would
            // always win an overlap, and the one underneath would be
            // unreachable. Anything else the DOM found is a room, an equipment
            // or an opening: those are deeper than a grouper by definition and
            // take the click unconditionally.
            if (!e.shiftKey && (!domKey || (domEl && isGrouperElement(domEl)))) {
                const grouper = this.pickGrouperAt(scr, domKey)
                if (grouper) {
                    this.select(grouper)
                    // The click that *found* an outline should not also drag
                    // it, so the view drag is armed instead of a move.
                    this.startDrag(e, { type: 'view', vx: this.view.x, vy: this.view.y })
                    return
                }
            }
            if (elementTarget && elementTarget.dataset.kind !== 'underlay') {
                const key = elementTarget.dataset.key as string
                if (e.shiftKey) {
                    // Shift builds a group; it never starts a drag, so the
                    // click cannot nudge what it was only meant to add.
                    this.toggleSelect(key)
                    return
                }
                // Alt turns the drag into a **resize** (spec: equipment is the
                // only thing with a footprint to stretch). It takes the group
                // down to the one element under the pointer on purpose:
                // scaling companions around their own anchors is not what
                // "make this one bigger" means, and Alt is already the
                // modifier that says *this* gesture is about the shape.
                const hit = elementByKey(this.doc, key)
                if (e.altKey && hit?.kind === 'equipment') {
                    this.select(key, null, true)
                    this.startHandleDrag(e, 'esize')
                    return
                }
                if (!this.selectedKeys().includes(key)) this.select(key, null, true)
                this.startMoveDrag(e, key)
                return
            }
            // Empty canvas, or the underlay: Shift rubber-bands, everything
            // else drags the view. A shift-press that never moves stays a
            // click and does nothing — it must not clear the group the user is
            // in the middle of building (§4).
            if (e.shiftKey) {
                this.marquee = { a: scr, b: scr }
                this.startDrag(e, { type: 'marquee', mScr0: scr })
                return
            }
            this.startDrag(e, { type: 'view', vx: this.view.x, vy: this.view.y, deselectOnClick: true })
            return
        }

        if (this.tool === 'wall') return this.wallToolClick(scr)
        if (this.tool === 'room' || this.tool === 'unit' || this.tool === 'hole') return this.roomToolClick(scr)
        if (this.tool === 'door' || this.tool === 'window') return this.openingToolClick()
        if (this.tool === 'equipment') return this.equipmentToolClick(scr)
        if (this.tool === 'run') return this.runToolClick(scr)
        if (this.tool === 'riser') return this.riserToolClick(scr)
    }

    private startDrag(e: PointerEvent, d: Partial<DragState> & { type: DragState['type'] }): void {
        this.drag = { ...d, x0: e.clientX, y0: e.clientY, moved: false } as DragState
        try {
            this.svg?.setPointerCapture(e.pointerId)
        } catch {
            /* capture is best-effort */
        }
    }

    private startMoveDrag(e: PointerEvent, key: string): void {
        const el = elementByKey(this.doc, key)
        if (!el) return
        const clone = (x: EditorElement): EditorElement => JSON.parse(JSON.stringify(x)) as EditorElement
        this.startDrag(e, {
            type: 'move',
            key,
            snapshot: this.snapshot(),
            orig: clone(el),
            // The rest of the group travels with the one under the pointer, by
            // the same delta — snapping is decided once, by the element being
            // dragged, and the companions keep their relative places.
            companions: this.selectedKeys()
                .filter((k) => k !== key)
                .map((k) => ({ key: k, orig: elementByKey(this.doc, k) }))
                .filter((c): c is { key: string; orig: EditorElement } => !!c.orig)
                .map((c) => ({ key: c.key, orig: clone(c.orig) })),
            mm0: this.scrToMm(this.eventScr(e)),
        })
    }

    private startHandleDrag(e: PointerEvent, handle: string): void {
        if (!this.selectedKey) return
        this.startDrag(e, {
            type: 'handle',
            handle,
            key: this.selectedKey,
            snapshot: this.snapshot(),
            mm0: this.scrToMm(this.eventScr(e)),
        })
        const el = elementByKey(this.doc, this.selectedKey)
        if (handle.startsWith('m') && el?.kind === 'space' && this.drag) {
            // A mid-handle inserts its vertex immediately and drags it.
            const i = Number(handle.slice(1))
            const a = el.boundary[i]
            const b = el.boundary[(i + 1) % el.boundary.length]
            el.boundary.splice(i + 1, 0, { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 })
            this.drag.handle = `v${i + 1}`
            this.drag.inserted = true
            this.selectedVertex = i + 1
            this.selectedHole = null
            this.render()
        } else if (handle.startsWith('v') && el?.kind === 'space') {
            this.selectedVertex = Number(handle.slice(1))
            this.selectedHole = null
        } else if (HOLE_HANDLE.test(handle) && el?.kind === 'space' && this.drag) {
            const [, hs, part, vs] = HOLE_HANDLE.exec(handle) as RegExpExecArray
            const hi = Number(hs)
            const ring = el.holes?.[hi]
            if (ring) {
                let i = Number(vs)
                if (part === 'm') {
                    // Same as the boundary's: a mid-handle inserts its corner
                    // at once and drags it.
                    const a = ring[i]
                    const b = ring[(i + 1) % ring.length]
                    ring.splice(i + 1, 0, { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 })
                    i += 1
                    this.drag.handle = `h${hi}v${i}`
                    this.drag.inserted = true
                }
                this.selectedHole = hi
                this.selectedVertex = i
                if (part === 'm') this.render()
            }
        } else if (handle.startsWith('rm') && el?.kind === 'run' && this.drag) {
            const i = Number(handle.slice(2))
            const a = el.points[i]
            const b = el.points[i + 1]
            if (a && b) {
                el.points.splice(i + 1, 0, { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 })
                this.drag.handle = `r${i + 1}`
                this.drag.inserted = true
                this.selectedVertex = i + 1
                this.render()
            }
        } else if (handle.startsWith('r') && el?.kind === 'run') {
            this.selectedVertex = Number(handle.slice(1))
        }
    }

    private onPointerMove = (e: PointerEvent): void => {
        const scr = this.eventScr(e)
        this.lastPointerMm = this.scrToMm(scr)
        const d = this.drag
        if (!d) {
            this.hoverEventTarget = e.target
            this.hoverPreview(scr)
            return
        }
        const dx = e.clientX - d.x0
        const dy = e.clientY - d.y0
        if (!d.moved && Math.hypot(dx, dy) < 3) return
        // The pointer is doing something else now, and every frame of a drag is
        // a render — which would restart the animation over and over.
        this.blinkKey = null
        d.moved = true

        if (d.type === 'view') {
            this.view.x = (d.vx ?? 0) + dx
            this.view.y = (d.vy ?? 0) + dy
            this.render()
            return
        }
        if (d.type === 'marquee') {
            this.marquee = { a: d.mScr0 ?? scr, b: scr }
            this.render()
            return
        }
        if (d.type === 'move') this.dragMove(d, scr)
        if (d.type === 'handle') this.dragHandle(d, scr)
        if (d.type === 'layer') this.dragLayer(d, scr)
    }

    private onPointerUp = (): void => {
        const d = this.drag
        this.drag = null
        this.snapIndicator = null
        const double = this.pendingDouble
        this.pendingDouble = null
        // The second click of a pair, released without a drag: now it is safe
        // to read it as a double click.
        if (double && !d?.moved && this.handleDoubleClick(double.target, double.scr)) return
        if (!d) return
        if (d.type === 'marquee') {
            const band = this.marquee
            this.marquee = null
            if (d.moved && band) this.applyMarquee(band.a, band.b)
            else this.render()
            return
        }
        if (d.type === 'view') {
            if (!d.moved && d.deselectOnClick) this.select(null)
            else this.render()
            return
        }
        if (!d.moved && !d.inserted) {
            this.render()
            return
        }
        if (d.snapshot) this.history.push(d.snapshot)
        // Where a thing ended up is what decides which lot it belongs to
        // (spec 2026-08-30-03 §9) — dragging a room into a flat's outline files
        // it there, and dragging the outline over a room takes the room in.
        // Before `commit`, so the reconciliation travels with the same undo
        // step as the move that caused it.
        if (d.type === 'move' || d.type === 'handle') this.reconcileGroupings()
        this.commit()
        if (d.type === 'move' || d.type === 'handle') {
            const moved = elementByKey(this.doc, this.selectedKey)
            if (moved?.kind === 'equipment') this.hooks.onEquipmentMoved?.(moved.key)
        }
    }

    /**
     * Re-file every drawn space under the grouper outline it now sits in
     * (spec 2026-08-30-03 §9). Runs after a move in either direction, so it
     * does not care which of the two was dragged.
     *
     * It only ever **assigns**. A space dragged out of every outline keeps the
     * membership it had, because a lot with no drawn outline is perfectly
     * normal — most are — and silently clearing the combobox's work on a nudge
     * would be a data-losing surprise. Groupers themselves are never filed:
     * nesting a common area inside a lot is a drawing, not a membership.
     */
    private reconcileGroupings(): boolean {
        let changed = false
        for (const el of this.doc.elements) {
            if (el.kind !== 'space' || isGrouperElement(el) || el.boundary.length < 3) continue
            const host = containingGrouper(this.doc, centroid(el.boundary), el.key)
            if (!host) continue
            const ref = grouperRefOf(host)
            if (!ref) continue
            const current = el.owner.grouper
            if (grouperIdOf(current) === grouperIdOf(ref)) continue
            // The membership row the previous grouper owned has to go with it,
            // and only the *saved* one is a row at all.
            el.owner.grouper = { ...ref, ...(current?.relationUid ? { relationUid: current.relationUid } : {}) }
            changed = true
        }
        return changed
    }

    private dragMove(d: DragState, scr: Point): void {
        const el = elementByKey(this.doc, d.key)
        const orig = d.orig
        if (!el || !orig || !d.mm0) return
        const mm = this.scrToMm(scr)
        const delta = { x: mm.x - d.mm0.x, y: mm.y - d.mm0.y }
        this.snapIndicator = null
        const anchorBefore = originOf(orig)

        if (el.kind === 'wall' && orig.kind === 'wall') {
            const rawStart = { x: orig.start.x + delta.x, y: orig.start.y + delta.y }
            const snapped = this.snap(rawStart, undefined, { key: el.key })
            const adj = { x: snapped.pt.x - rawStart.x, y: snapped.pt.y - rawStart.y }
            el.start = snapped.pt
            el.end = { x: orig.end.x + delta.x + adj.x, y: orig.end.y + delta.y + adj.y }
            this.snapIndicator = snapped.snappedTo
            this.reprojectOpenings(el)
        } else if (el.kind === 'space' && orig.kind === 'space') {
            el.boundary = orig.boundary.map((p) => ({ x: p.x + delta.x, y: p.y + delta.y }))
        } else if (el.kind === 'opening') {
            this.slideOpening(el, mm)
        } else if (el.kind === 'equipment' && orig.kind === 'equipment') {
            const snapped = this.snap({ x: orig.position.x + delta.x, y: orig.position.y + delta.y }, undefined, { key: el.key })
            el.position = snapped.pt
            this.snapIndicator = snapped.snappedTo
        } else if (el.kind === 'run' && orig.kind === 'run') {
            el.points = orig.points.map((p) => ({ x: p.x + delta.x, y: p.y + delta.y }))
        } else if (el.kind === 'riser' && orig.kind === 'riser') {
            const snapped = this.snap({ x: orig.position.x + delta.x, y: orig.position.y + delta.y }, undefined, { key: el.key })
            el.position = snapped.pt
            this.snapIndicator = snapped.snappedTo
        }
        if (d.companions?.length) {
            const applied = {
                x: originOf(el).x - anchorBefore.x,
                y: originOf(el).y - anchorBefore.y,
            }
            for (const c of d.companions) {
                const target = elementByKey(this.doc, c.key)
                if (!target) continue
                if (target.kind === 'opening' && c.orig.kind === 'opening') {
                    // An opening lives *on* a wall, so it follows by sliding
                    // along its own one rather than being shoved sideways off it.
                    this.slideOpening(target, {
                        x: c.orig.center.x + applied.x,
                        y: c.orig.center.y + applied.y,
                    })
                    continue
                }
                placeFrom(target, c.orig, applied)
            }
        }
        this.render()
    }

    private dragHandle(d: DragState, scr: Point): void {
        const el = elementByKey(this.doc, d.key)
        if (!el) return
        const mm = this.scrToMm(scr)
        this.snapIndicator = null
        const h = d.handle ?? ''

        if (el.kind === 'wall' && (h === 'w0' || h === 'w1')) {
            const vi = h === 'w1' ? 1 : 0
            const anchor = vi === 0 ? el.end : el.start
            const { pt, snappedTo } = this.snap(mm, anchor, { key: el.key, vi })
            if (vi === 0) el.start = pt
            else el.end = pt
            this.snapIndicator = snappedTo
            this.reprojectOpenings(el)
        } else if (el.kind === 'space' && h.startsWith('v')) {
            const vi = Number(h.slice(1))
            const n = el.boundary.length
            const anchor = el.boundary[(vi - 1 + n) % n]
            const { pt, snappedTo } = this.snap(mm, anchor, { key: el.key, vi })
            el.boundary[vi] = pt
            this.snapIndicator = snappedTo
        } else if (el.kind === 'space' && HOLE_HANDLE.test(h)) {
            const [, hs, , vs] = HOLE_HANDLE.exec(h) as RegExpExecArray
            const hi = Number(hs)
            const vi = Number(vs)
            const ring = el.holes?.[hi]
            if (ring) {
                const anchor = ring[(vi - 1 + ring.length) % ring.length]
                const { pt, snappedTo } = this.snap(mm, anchor, { key: el.key, vi: holeVertexIndex(el, hi, vi) })
                ring[vi] = pt
                this.snapIndicator = snappedTo
            }
        } else if (el.kind === 'opening' && (h === 'oc' || h === 'ow0' || h === 'ow1')) {
            if (h === 'oc') {
                this.slideOpening(el, mm)
            } else {
                // Width handle: the opposite edge stays put, the width is the
                // distance to it along the wall, rounded to 10 mm (as units).
                const rad = (el.directionDeg * Math.PI) / 180
                const ux = Math.cos(rad)
                const uy = Math.sin(rad)
                const sgn = h === 'ow0' ? -1 : 1
                const hw = el.width / 2
                const fixed = { x: el.center.x - sgn * ux * hw, y: el.center.y - sgn * uy * hw }
                const host = el.wallKey ? elementByKey(this.doc, el.wallKey) : undefined
                const projected = host && host.kind === 'wall' ? projectToLine(mm, host.start, host.end).pt : mm
                const upm = this.unitsPerMm()
                const newW = Math.max(50 * upm, Math.round(dist(projected, fixed) / (10 * upm)) * 10 * upm)
                el.width = newW
                el.center = { x: fixed.x + sgn * ux * newW / 2, y: fixed.y + sgn * uy * newW / 2 }
                // Remembered in millimetres, the unit of the habit.
                const widthMm = Math.round(this.unitsToMm(newW))
                if (el.openingKind === 'door') this.lastDoorWidth = widthMm
                else this.lastWindowWidth = widthMm
            }
        } else if (el.kind === 'run' && /^r\d+$/.test(h)) {
            const vi = Number(h.slice(1))
            const anchor = el.points[vi - 1] ?? el.points[vi + 1]
            const { pt, snappedTo } = this.snap(mm, anchor, { key: el.key, vi })
            el.points[vi] = pt
            this.snapIndicator = snappedTo
        } else if (el.kind === 'equipment' && h === 'ec') {
            const { pt, snappedTo } = this.snap(mm, undefined, { key: el.key })
            el.position = pt
            this.snapIndicator = snappedTo
        } else if (el.kind === 'equipment' && h === 'esize') {
            // Free on both axes — a symbol is drawn to a viewBox and stretches
            // to whatever the pointer asks for — and snapping stays out of it,
            // the way it stays out of an opening's width handle: the point of
            // the gesture is the exact size.
            const { width, depth } = equipmentSizeAt(el, el.typeCode ? this.doc.symbols[el.typeCode] : undefined, mm, this.unitsPerMm())
            el.width = width
            el.depth = depth
        } else if (el.kind === 'equipment' && h === 'erot') {
            // 15° snap by default, free with Alt (§8).
            const raw = (Math.atan2(mm.y - el.position.y, mm.x - el.position.x) * 180) / Math.PI - 90
            el.rotationDeg = this.altDown ? raw : Math.round(raw / 15) * 15
        }
        this.render()
    }

    /** Slides an opening along its host wall's line, keeping it glued to it. */
    private slideOpening(el: OpeningElement, mm: Point): void {
        const host = el.wallKey ? elementByKey(this.doc, el.wallKey) : undefined
        if (host && host.kind === 'wall') {
            const { center } = openingPlacement(host, mm, el.width)
            el.center = center
            el.directionDeg = wallDirectionDeg(host)
            el.wallThickness = host.thickness
        } else {
            el.center = mm
        }
    }

    /** Keeps a wall's openings on its line after the wall itself moved. */
    private reprojectOpenings(wall: WallElement): void {
        for (const el of this.doc.elements) {
            if (el.kind !== 'opening' || el.wallKey !== wall.key) continue
            el.center = projectToLine(el.center, wall.start, wall.end).pt
            el.directionDeg = wallDirectionDeg(wall)
            el.wallThickness = wall.thickness
        }
    }

    /* ------------------------------------------------------------- previewing */

    private hoverPreview(scr: Point): void {
        const mm = this.scrToMm(scr)
        let dirty = false

        if (this.drawing?.kind === 'wall' && this.drawing.last) {
            const { pt, snappedTo } = this.snap(mm, this.drawing.last)
            this.drawing.cur = pt
            this.snapIndicator = snappedTo
            dirty = true
        } else if ((this.drawing?.kind === 'room' || this.drawing?.kind === 'hole' || this.drawing?.kind === 'run') && this.drawing.pts?.length) {
            const anchor = this.drawing.pts[this.drawing.pts.length - 1]
            const { pt, snappedTo } = this.snap(mm, anchor)
            this.drawing.cur = pt
            this.snapIndicator = snappedTo
            dirty = true
        } else if (this.tool === 'calibrate' && this.measure?.p1) {
            this.measure.cur = mm
            dirty = true
        } else if (this.tool === 'door' || this.tool === 'window') {
            const near = nearestWall(this.doc, mm, this.view.k, OPENING_PICK_TOLERANCE_PX)
            if (near) {
                const width = this.mmToUnits(this.tool === 'door' ? this.lastDoorWidth : this.lastWindowWidth)
                const { center } = openingPlacement(near.wall, mm, width)
                const directionDeg = wallDirectionDeg(near.wall)
                const rad = (directionDeg * Math.PI) / 180
                const a = this.mmToScr({ x: center.x - (Math.cos(rad) * width) / 2, y: center.y - (Math.sin(rad) * width) / 2 })
                const b = this.mmToScr({ x: center.x + (Math.cos(rad) * width) / 2, y: center.y + (Math.sin(rad) * width) / 2 })
                this.ghost = `<line class="plan-ghost ${this.tool}" x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}"/>`
                this.ghostWall = { wallKey: near.wall.key, center, directionDeg, thickness: near.wall.thickness }
            } else {
                this.ghost = null
                this.ghostWall = null
            }
            dirty = true
        }
        // Hover feedback under the select tool (spec 2026-08-30-03 §8). The
        // element under the pointer is whatever the DOM says, plus the grouper
        // fallback, so what lights up is exactly what a click would take. Only
        // a *change* re-renders: pointermove fires per pixel, `render` rebuilds
        // every layer's markup.
        if (this.tool === 'select' && !this.drag) {
            const walking = this.ambiguity
            if (walking && Math.hypot(walking.scr.x - scr.x, walking.scr.y - scr.y) > AMBIGUITY_TOLERANCE_PX) {
                this.ambiguity = null
                dirty = true
                this.render()
            }
            const next = this.hoverTargetFor(this.hoverEventTarget, scr)
            if (next !== this.hoverKey) {
                this.hoverKey = next
                this.render()
            }
        } else if (this.hoverKey !== null) {
            this.hoverKey = null
            this.render()
        }
        if (dirty && this.hud) this.hud.innerHTML = this.renderHud()
    }

    /**
     * The grouper a click at this point takes, walking the overlap (§7).
     *
     * The first click on a spot takes the smallest outline containing it. Click
     * again without moving and the next one comes up, wrapping at the end — so
     * a lot hidden under a bigger one is reachable, and the red highlight says
     * there is something to reach. Moving the pointer more than a few pixels
     * starts a new question.
     */
    private pickGrouperAt(scr: Point, domKey?: string): string | null {
        // The same tolerance the drawing tools snap with, in millimetres at
        // this zoom — so "near enough to aim at" means the same thing whether
        // you are placing a corner or picking a lot.
        const hits = groupersAt(this.doc, this.scrToMm(scr), SNAP_TOLERANCE_PX / Math.max(this.view.k, 1e-9))
        // The edge the DOM found belongs in the walk even when the point is a
        // hair *outside* the polygon — clicking an outline is aiming at it.
        if (domKey && !hits.some((h) => h.key === domKey)) {
            const el = elementByKey(this.doc, domKey)
            if (el?.kind === 'space' && isGrouperElement(el)) hits.unshift(el)
        }
        if (!hits.length) {
            this.ambiguity = null
            return null
        }
        const keys = hits.map((h) => h.key)
        const previous = this.ambiguity
        const sameSpot = previous
            && Math.hypot(previous.scr.x - scr.x, previous.scr.y - scr.y) <= AMBIGUITY_TOLERANCE_PX
            && previous.keys.length === keys.length
            && previous.keys.every((k, i) => k === keys[i])
        const index = sameSpot ? (previous.index + 1) % keys.length : 0
        this.ambiguity = { scr, keys, index }
        // Clicking the same spot twice means two things, and only one of them
        // can happen: with outlines stacked here it is "show me the next one",
        // and with a single outline it is a double click asking to rename. The
        // overlap wins where there is one, because nothing else can reach the
        // shape underneath (the panel can always rename).
        if (keys.length > 1 && sameSpot) this.pendingDouble = null
        return keys[index]
    }

    /** Is the pointer still on the spot the current walk belongs to? */
    private atAmbiguitySpot(scr: Point): boolean {
        const a = this.ambiguity
        return !!a && Math.hypot(a.scr.x - scr.x, a.scr.y - scr.y) <= AMBIGUITY_TOLERANCE_PX
    }

    /** The overlapping outlines to paint red — only ever more than one (§7). */
    private get ambiguousKeys(): string[] {
        const a = this.ambiguity
        return a && a.keys.length > 1 ? a.keys : []
    }

    /**
     * What a click here would select: the element the DOM reports under the
     * pointer, and failing that the smallest grouper whose outline contains it.
     * The pointer's own target is used rather than `elementFromPoint` — the
     * browser has already done the hit-test, and it is the same answer
     * `onPointerDown` will act on, which is the point of the highlight.
     */
    private hoverTargetFor(target: EventTarget | null, scr: Point): string | null {
        const hit = (target as Element | null)?.closest?.('[data-key]') as HTMLElement | null
        if (hit && hit.dataset.kind !== 'underlay' && hit.dataset.key) return hit.dataset.key
        const tol = SNAP_TOLERANCE_PX / Math.max(this.view.k, 1e-9)
        return groupersAt(this.doc, this.scrToMm(scr), tol)[0]?.key ?? null
    }

    /* ------------------------------------------------------------ drawing ops */

    private wallToolClick(scr: Point): void {
        const mm = this.scrToMm(scr)
        const d = this.drawing
        if (!d || d.kind !== 'wall' || !d.last) {
            const { pt } = this.snap(mm)
            this.drawing = { kind: 'wall', last: pt, cur: pt }
            this.render()
            return
        }
        const { pt } = this.snap(mm, d.last)
        if (dist(this.mmToScr(pt), this.mmToScr(d.last)) < MIN_SEGMENT_PX) return
        const from = { ...d.last }
        this.mutate(() => {
            const owner = this.hooks.wallOwner()
            const target = this.hooks.wallRepFor(owner)
            const taken = collectLocalIds(this.doc)
            const wall: WallElement = {
                key: nextKey('wall'),
                kind: 'wall',
                source: {
                    repUid: target?.repUid,
                    registrationUid: target?.registrationUid,
                    transform: target?.transform ?? IDENTITY,
                    localId: nextLocalId('wall', taken),
                },
                owner,
                start: from,
                end: { ...pt },
                thickness: this.lengthToUnits(this.lastThickness, MAX_THICKNESS_FRACTION),
            }
            this.doc.elements.push(wall)
        })
        this.drawing = { kind: 'wall', last: pt, cur: pt }
        this.render()
    }

    endWallChain(): void {
        this.drawing = null
        this.render()
    }

    /**
     * The run tool: click a vertex per bend, Enter or a double click finishes,
     * Escape abandons. Nothing reaches the document until the run is finished,
     * so one undo step takes the whole line back — the room gesture, open.
     */
    private runToolClick(scr: Point): void {
        const mm = this.scrToMm(scr)
        const d = this.drawing
        if (!d || d.kind !== 'run' || !d.pts) {
            const { pt } = this.snap(mm)
            this.drawing = { kind: 'run', pts: [pt], cur: pt }
            this.render()
            return
        }
        // The second press of a double click only ends the line (at release).
        if (this.pendingDouble) return
        const last = d.pts[d.pts.length - 1]
        const { pt } = this.snap(mm, last)
        if (dist(this.mmToScr(pt), this.mmToScr(last)) < MIN_SEGMENT_PX) return
        d.pts.push(pt)
        this.render()
    }

    /** Commits the run being drawn. Fewer than two points is a cancel. */
    finishRun(): void {
        const d = this.drawing
        this.drawing = null
        if (!d || d.kind !== 'run' || !d.pts || d.pts.length < 2) {
            this.render()
            return
        }
        const pts = d.pts
        const defaults = this.hooks.runDefaults?.() ?? {}
        let created: string | null = null
        this.mutate(() => {
            // A run joins the frame's structure bundle, exactly like a wall:
            // the network asset it will depict is a link, not a home.
            const owner = this.hooks.wallOwner()
            const target = this.hooks.wallRepFor(owner)
            const taken = collectLocalIds(this.doc)
            const run: RunElement = {
                key: nextKey('run'),
                kind: 'run',
                source: {
                    repUid: target?.repUid,
                    registrationUid: target?.registrationUid,
                    transform: target?.transform ?? IDENTITY,
                    localId: nextLocalId('run', taken),
                    repOwner: { ...owner },
                },
                owner,
                network: defaults.network || this.doc.networks?.[0]?.code || 'unknown',
                points: pts,
                ...(defaults.widthMm && defaults.widthMm > 0 ? { widthMm: defaults.widthMm } : {}),
                ...(defaults.level ? { level: defaults.level } : {}),
            }
            this.doc.elements.push(run)
            created = run.key
        })
        if (created) {
            this.select(created)
            this.hooks.onRunCreated?.(created)
        }
        this.render()
    }

    /**
     * The riser tool: one click places where a vertical run crosses this
     * floor. Like a run it joins the frame's structure bundle and depicts its
     * column by link, set in the inspector.
     */
    private riserToolClick(scr: Point): void {
        const { pt } = this.snap(this.scrToMm(scr))
        const defaults = this.hooks.riserDefaults?.() ?? {}
        let created: string | null = null
        this.mutate(() => {
            const repOwner = this.hooks.wallOwner()
            const target = this.hooks.wallRepFor(repOwner)
            const taken = collectLocalIds(this.doc)
            const riser: RiserElement = {
                key: nextKey('riser'),
                kind: 'riser',
                source: {
                    repUid: target?.repUid,
                    registrationUid: target?.registrationUid,
                    transform: target?.transform ?? IDENTITY,
                    localId: nextLocalId('riser', taken),
                    repOwner: { ...repOwner },
                },
                // Unlinked, it reads like a loaded one: the bundle's own owner.
                owner: { ...repOwner },
                network: defaults.network || this.doc.networks?.[0]?.code || 'unknown',
                position: pt,
                ...(defaults.diameterMm && defaults.diameterMm > 0 ? { diameterMm: defaults.diameterMm } : {}),
                ...(defaults.passage ? { passage: defaults.passage } : {}),
            }
            this.doc.elements.push(riser)
            created = riser.key
        })
        if (created) {
            this.select(created)
            this.hooks.onRiserCreated?.(created)
        }
        this.render()
    }

    /**
     * The polygon gesture, shared by the `room` and the `unit` tool
     * (spec 2026-08-30-01 §7): splitting a floor into flats and commons is the
     * same click-click-close drawing as outlining a room, and only the asset
     * type of what comes out differs.
     */
    private roomToolClick(scr: Point): void {
        const mm = this.scrToMm(scr)
        // A hole is traced exactly like a room; only what closing it does differs.
        const kind = this.tool === 'hole' ? 'hole' : 'room'
        const d = this.drawing
        if (!d || d.kind !== kind || !d.pts) {
            const { pt } = this.snap(mm)
            this.drawing = { kind, pts: [pt], cur: pt }
            this.render()
            return
        }
        // The second press of a double click only ends the shape (at release):
        // it adds no corner, so nothing has to be taken back afterwards and a
        // real corner is never mistaken for a duplicate.
        if (this.pendingDouble) return
        const firstScr = this.mmToScr(d.pts[0])
        if (d.pts.length >= 3 && dist(firstScr, scr) < 10) {
            if (kind === 'hole') this.closeHole()
            else this.closeRoom()
            return
        }
        const { pt } = this.snap(mm, d.pts[d.pts.length - 1])
        d.pts.push(pt)
        this.render()
    }

    closeRoom(): void {
        const d = this.drawing
        if (!d || d.kind !== 'room' || !d.pts || d.pts.length < 3) {
            this.drawing = null
            this.render()
            return
        }
        const pts = d.pts
        const drawingGrouper = this.tool === 'unit'
        this.drawing = null
        let created: string | null = null
        this.mutate(() => {
            const taken = collectLocalIds(this.doc)
            const localId = nextLocalId('space', taken)
            const centre = pts.reduce((s, p) => ({ x: s.x + p.x / pts.length, y: s.y + p.y / pts.length }), { x: 0, y: 0 })
            const el: SpaceElement = {
                key: nextKey('space'),
                kind: 'space',
                source: { transform: IDENTITY, localId },
                owner: this.hooks.createOwner(drawingGrouper ? 'grouper' : 'space', centre),
                name: '',
                boundary: pts,
            }
            // A room drawn inside a flat's outline joins that flat, with no
            // gesture of its own (spec 2026-08-30-01 §6). A grouper never
            // auto-joins another grouper: nesting outlines is a legitimate way
            // to draw a common area inside a lot, not a membership statement.
            if (!drawingGrouper) {
                const host = containingGrouper(this.doc, centre, el.key)
                if (host) {
                    const ref = grouperRefOf(host)
                    if (ref) el.owner.grouper = ref
                }
            }
            this.doc.elements.push(el)
            // Drawing a lot *around* rooms that are already there files them
            // under it (spec 2026-08-30-03 §9) — the whole point of splitting a
            // floor that was drawn room-first.
            if (drawingGrouper) this.reconcileGroupings()
            created = el.key
        })
        if (created) {
            // Drawing a room *is* a click on a room, and the unit it just joined
            // is exactly what the click leaves in question — so it blinks, while
            // drawing a unit (a grouper) blinks nothing.
            this.select(created, null, true)
            this.hooks.onRoomCreated(created)
        }
    }

    /**
     * Cuts the traced ring out of the space it lies in (spec 2026-10-19-07).
     * The space is found, not picked: the **smallest** one the ring fits in, so
     * a pillar traced inside a room inside a lot cuts the room. A ring that fits
     * in none — outside every room, across one's edge, over a hole already
     * there — is dropped whole rather than half-applied, and the shell says so.
     *
     * The tool stays armed: a floor's pillars come in rows.
     */
    closeHole(): void {
        const d = this.drawing
        this.drawing = null
        if (!d || d.kind !== 'hole' || !d.pts || d.pts.length < 3) {
            this.render()
            return
        }
        const ring = d.pts
        const host = spaceForHole(this.doc, ring)
        if (!host) {
            this.render()
            this.hooks.onHoleRejected?.()
            return
        }
        this.mutate(() => {
            host.holes = [...spaceHoles(host), ring]
        })
        this.select(host.key)
        this.hooks.onHoleCut?.(host.key)
    }

    private openingToolClick(): void {
        const ghost = this.ghostWall
        if (!ghost) return
        const openingKind: OpeningKind = this.tool === 'door' ? 'door' : 'window'
        let created: string | null = null
        // The wall this opening is cut into owns it (§2), so its asset is what
        // the parent rule starts from.
        const hostWall = elementByKey(this.doc, ghost.wallKey)
        const wallOwnerUid = hostWall ? repAssetUidOf(hostWall) : undefined
        this.mutate(() => {
            const taken = collectLocalIds(this.doc)
            const el: OpeningElement = {
                key: nextKey(openingKind),
                kind: 'opening',
                source: { transform: IDENTITY, localId: nextLocalId(openingKind, taken) },
                owner: this.hooks.createOwner('opening', ghost.center, {
                    openingKind,
                    wallOwnerUid,
                    wallThickness: ghost.thickness,
                }),
                openingKind,
                center: { ...ghost.center },
                directionDeg: ghost.directionDeg,
                width: this.lengthToUnits(
                    openingKind === 'door' ? this.lastDoorWidth : this.lastWindowWidth,
                    MAX_OPENING_FRACTION,
                ),
                wallThickness: ghost.thickness,
                swing: openingKind === 'door' ? 'left' : undefined,
                rotation: 0,
                wallKey: ghost.wallKey,
            }
            const host = elementByKey(this.doc, ghost.wallKey)
            if (host?.owner.assetUid) el.wallRef = { assetUid: host.owner.assetUid, wallId: host.source.localId }
            this.doc.elements.push(el)
            created = el.key
        })
        if (created) this.select(created)
    }

    private equipmentToolClick(scr: Point): void {
        const { pt } = this.snap(this.scrToMm(scr))
        // Armed before the click: the type the panel is holding, with its
        // footprint. Nothing armed still places a plain box and lets the shell
        // ask afterwards.
        const armed = this.hooks.equipmentDefaults?.() ?? {}
        let created: string | null = null
        const footprint = this.footprintToUnits(armed.width ?? 600, armed.depth ?? 300)
        this.mutate(() => {
            const taken = collectLocalIds(this.doc)
            this.doc.elements.push({
                key: nextKey('equipment'),
                kind: 'equipment',
                source: { transform: IDENTITY, localId: nextLocalId('equipment', taken) },
                owner: this.hooks.createOwner('equipment', pt, { typeCode: armed.typeCode }),
                position: pt,
                rotationDeg: 0,
                width: footprint.width,
                depth: footprint.depth,
                ...(armed.typeCode ? { typeCode: armed.typeCode } : {}),
            })
            created = this.doc.elements[this.doc.elements.length - 1].key
        })
        if (created) {
            // Same as a room: the drop answers "which room did it land in?".
            this.select(created, null, true)
            if (!armed.typeCode) this.hooks.onEquipmentPlaced(created)
        }
    }

    /* ------------------------------------------------- calibration (§1, §2) */

    /**
     * Picks the registration to calibrate. Any registration in the scene
     * qualifies — a raster underlay just as much as another asset's vector
     * layer (§1) — and switching target starts a fresh pair set.
     */
    setCalibrationTarget(registrationUid: string | null): void {
        this.calibrationUid = registrationUid
        this.calibrationBase = registrationUid ? layerTransform(this.doc, registrationUid) : null
        this.pairs = []
        this.pendingPair = null
        this.measure = null
        this.render()
        this.emitCalibration()
    }

    /**
     * Flashes a segment over the plan for `ms`, then clears it
     * (spec 2026-09-05-07 §4). Purely a HUD decoration — no document state, so
     * it is invisible to the diff, to undo and to save.
     */
    flashMeasure(a: Point, b: Point, ms = 6000): void {
        if (this.flashTimer !== null) window.clearTimeout(this.flashTimer)
        this.flashSegment = { a: { ...a }, b: { ...b } }
        this.render()
        this.flashTimer = window.setTimeout(() => {
            this.flashSegment = null
            this.flashTimer = null
            this.render()
        }, ms)
    }

    /**
     * Shows a proposed calibration's circle — `a`/`b` its horizontal diameter
     * in frame units — and brings it into view, a few diameters across.
     */
    showCalibrationProposal(a: Point, b: Point): void {
        this.proposalSegment = { a: { ...a }, b: { ...b } }
        const r = this.svg?.getBoundingClientRect()
        const radius = Math.hypot(b.x - a.x, b.y - a.y) / 2
        if (r && r.width >= 40 && r.height >= 40 && radius > 0) {
            const cx = (a.x + b.x) / 2
            const cy = (a.y + b.y) / 2
            // Not fitScale: its 1-unit floor is meant for millimetre scenes,
            // and a frame's units can be far larger than a millimetre.
            const span = radius * 8
            const k = Math.min(r.width, r.height) / span
            this.view = { k, x: r.width / 2 - cx * k, y: r.height / 2 - cy * k }
        }
        this.render()
    }

    /** Removes the proposed calibration's circle. */
    clearCalibrationProposal(): void {
        if (!this.proposalSegment) return
        this.proposalSegment = null
        this.render()
    }

    /** Enters calibrate mode, optionally on a given registration (deep link). */
    beginCalibration(registrationUid?: string): void {
        if (registrationUid) this.setCalibrationTarget(registrationUid)
        this.setTool('calibrate')
    }

    /** Calibration vs. placement for the current target (§3). */
    get calibrationMode(): 'calibration' | 'placement' {
        return calibrationModeFor(this.calibrationUid, this.doc.primaryRegistrationUid)
    }

    get calibration(): CalibrationState {
        const mode = this.calibrationMode
        const dirty = mode === 'calibration'
            ? this.doc.calibrationEdit !== null && this.doc.calibrationEdit !== undefined
            : this.calibrationUid ? this.doc.registrationEdits[this.calibrationUid] !== undefined : false
        return {
            registrationUid: this.calibrationUid,
            pairs: this.pairs.map((p) => ({ source: { ...p.source }, target: { ...p.target } })),
            awaitingTarget: this.pendingPair !== null,
            measuring: this.measure !== null,
            dirty,
            mode,
            mmPerUnit: mmPerUnit(this.doc.calibration),
            current: this.currentCalibration,
            showCurrent: this.showCurrentCalibration,
        }
    }

    /**
     * What the frame is calibrated to right now: the pending edit when there
     * is one, so applying a measure replaces the drawn vector, and Cancel —
     * which reverts the edit — brings the old one straight back.
     */
    private get currentCalibration(): FrameCalibration | null {
        const edit = this.doc.calibrationEdit
        return (edit ? edit.calibration : this.doc.calibration) ?? null
    }

    /** The "show on plan" toggle beside the calibration readout (§3). */
    setShowCurrentCalibration(on: boolean): void {
        this.showCurrentCalibration = on
        this.render()
        this.emitCalibration()
    }

    private emitCalibration(): void {
        this.hooks.onCalibrationChange?.(this.calibration)
    }

    /** Starts the measure-scale sub-mode: two clicks, then a real distance. */
    startMeasure(): void {
        if (!this.calibrationUid) return
        this.measure = {}
        this.pendingPair = null
        this.render()
        this.emitCalibration()
    }

    /**
     * The measure tool's two meanings (spec 2026-08-25-04 §3).
     *
     * On the **primary plan** it is a *calibration*: the ratio between the
     * distance the user measured on screen and the real one becomes the
     * frame's `mm_per_unit`, and **nothing moves** — the underlay and the
     * geometry traced over it stay exactly where they are. On any other layer
     * it stays *placement*: the layer is rescaled about the segment's midpoint,
     * which is how a mis-sized secondary plan is fitted onto the drawing.
     */
    applyMeasure(a: Point, b: Point, realDistanceMm: number): void {
        const uid = this.calibrationUid
        if (!uid) return
        if (this.calibrationMode === 'calibration') {
            const calibration = calibrationFromMeasure(a, b, realDistanceMm)
            if (calibration) {
                this.mutate(() => applySceneCalibration(this.doc, uid, calibration))
            }
            this.measure = null
            this.emitCalibration()
            return
        }
        const current = layerTransform(this.doc, uid)
        if (!current) return
        const next = measureScaleTransform(current, a, b, realDistanceMm)
        this.mutate(() => applyLayerTransform(this.doc, uid, next, { method: 'manual', confidence: 'high' }))
        this.measure = null
        this.emitCalibration()
    }

    /**
     * Writes a typed "1:N" scale as the frame's calibration (spec
     * 2026-10-16-06). Calibration mode only: nothing moves.
     */
    applyStatedScale(calibration: FrameCalibration): void {
        const uid = this.calibrationUid
        if (!uid || this.calibrationMode !== 'calibration') return
        this.mutate(() => applySceneCalibration(this.doc, uid, calibration))
        this.emitCalibration()
    }

    /** Drops the accumulated pairs, keeping the placement they produced. */
    clearPairs(): void {
        this.pairs = []
        this.pendingPair = null
        this.render()
        this.emitCalibration()
    }

    /**
     * Is there calibration work for Escape to back out of?
     *
     * Without the question the ladder gets stuck: `cancelCalibration` always
     * does *something*, so an idle calibrate tool would answer every Escape
     * with a reset and never hand the press on to the tool below it.
     */
    private get calibrationPending(): boolean {
        const uid = this.calibrationUid
        return Boolean(
            this.pairs.length || this.pendingPair || this.measure || this.doc.calibrationEdit ||
            (uid && this.doc.registrationEdits[uid]),
        )
    }

    /** Esc: puts the layer — or the calibration — back where it was found. */
    cancelCalibration(): void {
        const uid = this.calibrationUid
        if (uid && this.calibrationMode === 'calibration' && this.doc.calibrationEdit) {
            this.mutate(() => revertSceneCalibration(this.doc))
            this.pairs = []
            this.pendingPair = null
            this.measure = null
            this.render()
            this.emitCalibration()
            return
        }
        if (uid && this.calibrationBase && this.doc.registrationEdits[uid]) {
            const base = this.calibrationBase
            this.mutate(() => {
                applyLayerTransform(this.doc, uid, base, { method: 'manual' })
                delete this.doc.registrationEdits[uid]
            })
        }
        this.pairs = []
        this.pendingPair = null
        this.measure = null
        this.render()
        this.emitCalibration()
    }

    /**
     * Enter: the calibration is settled. The pairs stop accumulating and the
     * pending registration edit is handed to the shell, which writes it.
     */
    finishCalibration(): CalibrationCommit | null {
        const uid = this.calibrationUid
        const mode = this.calibrationMode
        const edit = uid ? this.doc.registrationEdits[uid] : undefined
        const calibrationEdit = this.doc.calibrationEdit
        this.pairs = []
        this.pendingPair = null
        this.measure = null
        this.calibrationBase = uid ? layerTransform(this.doc, uid) : null
        this.render()
        this.emitCalibration()
        if (mode === 'calibration') {
            return calibrationEdit
                ? { kind: 'calibration', registrationUid: calibrationEdit.registrationUid, calibration: calibrationEdit.calibration }
                : null
        }
        return edit ? { kind: 'placement', registrationUid: edit.registrationUid } : null
    }

    private calibrateClick(scr: Point): void {
        const uid = this.calibrationUid
        if (!uid) return
        const raw = this.scrToMm(scr)
        if (this.measure) return this.measureClick(raw)

        const current = layerTransform(this.doc, uid)
        if (!current) return

        // Calibrating the primary plan: both ends of a pair live in the
        // frame's own plan units, and the fit's scale is the correction to
        // `mm_per_unit`. Nothing is transformed — that is the whole point (§3).
        if (this.calibrationMode === 'calibration') {
            if (!this.pendingPair) {
                const { pt } = snapPoint(raw, this.doc, {
                    scale: this.view.k,
                    enabled: this.snapEnabled && !this.altDown,
                })
                const onLayer = this.nearestLayerPoint(uid, raw) ?? pt
                this.pendingPair = { source: onLayer, frame: onLayer }
                this.render()
                this.emitCalibration()
                return
            }
            const { pt: target } = snapPoint(raw, this.doc, {
                scale: this.view.k,
                enabled: this.snapEnabled && !this.altDown,
                excludeRegistrationUid: uid,
            })
            this.pairs.push({ source: this.pendingPair.source, target })
            this.pendingPair = null
            const base = this.doc.calibrationEdit?.base ?? this.doc.calibration ?? null
            const calibration = calibrationFromPairs(this.pairs, base)
            if (calibration) this.mutate(() => applySceneCalibration(this.doc, uid, calibration))
            else this.render()
            this.emitCalibration()
            return
        }

        if (!this.pendingPair) {
            // First click: a point *on the layer*. Its own vertices snap (an
            // underlay has none, so any pixel does).
            const { pt } = snapPoint(raw, this.doc, {
                scale: this.view.k,
                enabled: this.snapEnabled && !this.altDown,
            })
            const onLayer = this.nearestLayerPoint(uid, raw) ?? pt
            this.pendingPair = { source: applyTransform(invertTransform(current), onLayer), frame: onLayer }
            this.render()
            this.emitCalibration()
            return
        }

        // Second click: where that point truly belongs, snapped to the *rest*
        // of the scene.
        const { pt: target } = snapPoint(raw, this.doc, {
            scale: this.view.k,
            enabled: this.snapEnabled && !this.altDown,
            excludeRegistrationUid: uid,
        })
        this.pairs.push({ source: this.pendingPair.source, target })
        this.pendingPair = null
        const base = this.calibrationBase ?? current
        const solved = solveSimilarity(this.pairs, base)
        this.mutate(() => applyLayerTransform(this.doc, uid, solved, {
            method: 'anchor_pairs',
            confidence: this.pairs.length >= 2 ? 'high' : 'medium',
            pairs: this.pairs.map((p) => ({ source: { ...p.source }, target: { ...p.target } })),
        }))
        this.emitCalibration()
    }

    private measureClick(mm: Point): void {
        if (!this.measure) return
        if (!this.measure.p1) {
            this.measure.p1 = mm
            this.render()
            this.emitCalibration()
            return
        }
        const p1 = this.measure.p1
        if (dist(p1, mm) < 1e-6) return
        this.measure = { p1, cur: mm }
        this.hooks.onMeasureReady?.(p1, mm)
    }

    /** The layer point closest to a click, so pairs anchor on real geometry. */
    private nearestLayerPoint(registrationUid: string, mm: Point): Point | null {
        const tol = 14 / Math.max(this.view.k, 1e-9)
        let best: Point | null = null
        let bestD = tol
        for (const p of layerPoints(this.doc, registrationUid)) {
            const d = dist(mm, p)
            if (d < bestD) {
                bestD = d
                best = p
            }
        }
        return best
    }

    /* ------------------------------------------- layer manipulation (§6) */

    private startLayerDrag(e: PointerEvent, handle: string): void {
        const uid = this.selectedLayerUid
        if (!uid) return
        const base = layerTransform(this.doc, uid)
        const bb = layerBBox(this.doc, uid)
        if (!base || !bb) return
        const pivot = { x: (bb.x0 + bb.x1) / 2, y: (bb.y0 + bb.y1) / 2 }
        const mm0 = this.scrToMm(this.eventScr(e))
        this.startDrag(e, {
            type: 'layer',
            handle,
            key: uid,
            snapshot: this.snapshot(),
            mm0,
            layerBase: { ...base },
            layerPivot: pivot,
            layerAngle0: toDeg(Math.atan2(mm0.y - pivot.y, mm0.x - pivot.x)),
        })
    }

    private dragLayer(d: DragState, scr: Point): void {
        const uid = d.key
        if (!uid || !d.layerBase || !d.layerPivot || !d.mm0) return
        const mm = this.scrToMm(scr)
        let next: Transform
        if (d.handle === 'lrot') {
            const raw = toDeg(Math.atan2(mm.y - d.layerPivot.y, mm.x - d.layerPivot.x)) - (d.layerAngle0 ?? 0)
            const step = this.altDown ? raw : Math.round(raw / 15) * 15
            next = rotateAbout(d.layerBase, d.layerPivot, step)
        } else {
            let dx = mm.x - d.mm0.x
            let dy = mm.y - d.mm0.y
            if (this.shiftDown) {
                if (Math.abs(dx) > Math.abs(dy)) dy = 0
                else dx = 0
            }
            next = translateBy(d.layerBase, dx, dy)
        }
        applyLayerTransform(this.doc, uid, next, { method: 'manual', confidence: 'high' })
        this.render()
    }

    /* --------------------------------- promotion / demotion / retarget (§3–§5) */

    /** True when the selected element can leave its bundle for its own asset. */
    canPromoteSelected(): boolean {
        const el = elementByKey(this.doc, this.selectedKey)
        return !!el && canPromote(this.doc, el)
    }

    /** Extracts the selected element into its own representation (§3). */
    promoteSelected(owner: ElementOwner): boolean {
        const key = this.selectedKey
        if (!key) return false
        let done = false
        this.mutate(() => {
            done = promoteElement(this.doc, key, owner)
        })
        return done
    }

    /** The bundles the selected element could be merged back into (§4). */
    demoteTargets(): BundleTarget[] {
        return bundleTargets(this.doc, elementByKey(this.doc, this.selectedKey) ?? null)
    }

    /** Merges the selected element back into a bundle (§4). */
    demoteSelected(target: BundleTarget): boolean {
        const key = this.selectedKey
        if (!key) return false
        let done = false
        this.mutate(() => {
            done = demoteElement(this.doc, key, target)
        })
        return done
    }

    /** Moves the selected wall to another wall bundle, openings following (§5). */
    retargetSelectedWall(owner: ElementOwner): boolean {
        const key = this.selectedKey
        if (!key) return false
        let done = false
        this.mutate(() => {
            done = retargetWall(this.doc, key, owner)
        })
        return done
    }

    /* ------------------------------------------------------------- edit verbs */

    /**
     * The hand is for looking. Its pointer already only moves the view, and
     * this closes the other door: the keyboard verbs that would change the
     * drawing are inert while it is armed, so "explore" cannot quietly become
     * "edit" because a stray Delete or ⌘V arrived.
     *
     * Undo and redo are deliberately *not* gated — they navigate the history
     * rather than author anything, and the toolbar offers them in every tool.
     */
    private get exploring(): boolean {
        return this.tool === 'pan'
    }

    deleteSelected(): void {
        if (this.exploring) return
        const el = elementByKey(this.doc, this.selectedKey)
        if (!el) return
        // A hole's corner is removed the same way. A hole down to three corners
        // goes whole instead: two corners are not a hole, and the room around
        // it is not what the key was aimed at.
        if (el.kind === 'space' && this.selectedHole != null && this.selectedVertex != null) {
            const hi = this.selectedHole
            const vi = this.selectedVertex
            this.mutate(() => {
                const ring = el.holes?.[hi]
                if (ring && ring.length > 3) ring.splice(vi, 1)
                else removeHole(el, hi)
            })
            this.selectedHole = null
            this.selectedVertex = null
            this.render()
            return
        }
        // Vertex-first: with a room vertex selected, Delete removes the vertex.
        if (el.kind === 'space' && this.selectedVertex != null && el.boundary.length > 3) {
            const vi = this.selectedVertex
            this.mutate(() => el.boundary.splice(vi, 1))
            this.selectedVertex = null
            this.selectedHole = null
            this.render()
            return
        }
        // Same for a bend of a run, as long as a line is left.
        if (el.kind === 'run' && this.selectedVertex != null && el.points.length > 2) {
            const vi = this.selectedVertex
            this.mutate(() => el.points.splice(vi, 1))
            this.selectedVertex = null
            this.selectedHole = null
            this.render()
            return
        }
        const doomed = new Set(this.selectedKeys())
        this.mutate(() => {
            this.doc.elements = this.doc.elements.filter((e) => !doomed.has(e.key))
            for (const o of this.doc.elements) {
                if (o.kind === 'opening' && o.wallKey && doomed.has(o.wallKey)) o.wallKey = undefined
            }
        })
        this.select(null)
    }

    /**
     * Copy the selected equipment. Geometry and type only: pasting a radiator
     * makes *another* radiator, it does not add a second footprint of the one
     * that was copied — the same rule duplicate follows.
     *
     * Positions travel as offsets from the first item, so a pasted group keeps
     * its layout wherever it lands.
     */
    copySelection(): number {
        const items: ClipboardItem[] = []
        let anchor: Point | null = null
        for (const key of this.selectedKeys()) {
            const el = elementByKey(this.doc, key)
            if (el?.kind === 'equipment') {
                anchor ??= el.position
                items.push({
                    kind: 'equipment',
                    typeCode: el.typeCode,
                    width: el.width,
                    depth: el.depth,
                    rotationDeg: el.rotationDeg,
                    dx: el.position.x - anchor.x,
                    dy: el.position.y - anchor.y,
                })
                continue
            }
            // A door or a window travels with its semantics — a two-leaf
            // right-swinging door pastes as one (spec 2026-08-30-03 §14). Its
            // *host wall* does not travel: the paste re-hosts it on the wall it
            // lands on, since an opening cut into nothing is not an opening.
            if (el?.kind === 'opening') {
                anchor ??= el.center
                items.push({
                    kind: 'opening',
                    openingKind: el.openingKind,
                    width: el.width,
                    depth: 0,
                    rotationDeg: el.directionDeg,
                    wallThickness: el.wallThickness,
                    ...(el.operation ? { operation: el.operation } : {}),
                    ...(el.swing ? { swing: el.swing } : {}),
                    ...(el.leaves ? { leaves: el.leaves } : {}),
                    ...(el.rotation !== undefined ? { rotation: el.rotation } : {}),
                    dx: el.center.x - anchor.x,
                    dy: el.center.y - anchor.y,
                })
            }
        }
        if (!items.length) return 0
        this.clipboard = items
        this.hooks.onClipboardChange?.(items)
        return items.length
    }

    /**
     * Paste under the pointer, or beside the original when the pointer has not
     * been over the stage. Each item gets a fresh owner resolved *here*, so
     * pasting into another room files it in that room.
     */
    pasteClipboard(at?: Point): number {
        if (this.exploring || !this.clipboard.length) return 0
        const base = at ?? this.lastPointerMm ?? {
            x: (this.clipboard[0].dx ?? 0) + 300,
            y: (this.clipboard[0].dy ?? 0) + 300,
        }
        const created: string[] = []
        this.mutate(() => {
            for (const item of this.clipboard) {
                const taken = collectLocalIds(this.doc)
                const position = { x: base.x + item.dx, y: base.y + item.dy }
                if (item.kind === 'opening') {
                    const near = nearestWall(this.doc, position, this.view.k, OPENING_PICK_TOLERANCE_PX)
                    // No wall under the pointer: silently dropping the paste is
                    // better than leaving a door floating in a room, and the
                    // count returned says how many actually landed.
                    if (!near) continue
                    const { center } = openingPlacement(near.wall, position, item.width)
                    const openingKind = item.openingKind ?? 'door'
                    const el: OpeningElement = {
                        key: nextKey(openingKind),
                        kind: 'opening',
                        source: { transform: IDENTITY, localId: nextLocalId(openingKind, taken) },
                        owner: this.hooks.createOwner('opening', center, {
                            openingKind,
                            wallOwnerUid: repAssetUidOf(near.wall),
                            wallThickness: near.wall.thickness,
                        }),
                        openingKind,
                        center,
                        directionDeg: wallDirectionDeg(near.wall),
                        width: item.width,
                        wallThickness: near.wall.thickness,
                        ...(item.operation ? { operation: item.operation } : {}),
                        ...(item.swing ? { swing: item.swing } : {}),
                        ...(item.leaves ? { leaves: item.leaves } : {}),
                        ...(item.rotation !== undefined ? { rotation: item.rotation } : {}),
                        wallKey: near.wall.key,
                    }
                    if (near.wall.owner.assetUid) {
                        el.wallRef = { assetUid: near.wall.owner.assetUid, wallId: near.wall.source.localId }
                    }
                    this.doc.elements.push(el)
                    created.push(el.key)
                    continue
                }
                this.doc.elements.push({
                    key: nextKey('equipment'),
                    kind: 'equipment',
                    source: { transform: IDENTITY, localId: nextLocalId('equipment', taken) },
                    owner: this.hooks.createOwner('equipment', position, { typeCode: item.typeCode }),
                    position,
                    rotationDeg: item.rotationDeg,
                    width: item.width,
                    depth: item.depth,
                    ...(item.typeCode ? { typeCode: item.typeCode } : {}),
                })
                created.push(this.doc.elements[this.doc.elements.length - 1].key)
            }
        })
        if (!created.length) return 0
        // The paste is selected as a group, so it can be nudged into place.
        this.selectedKey = created[0]
        this.extraKeys = created.slice(1)
        this.selectedVertex = null
        this.selectedHole = null
        this.render()
        this.hooks.onSelectionChange(this.selectedKey, this.extraKeys)
        return created.length
    }

    /** Adopt a clipboard the shell restored — a copy made on another floor. */
    setClipboard(items: ClipboardItem[]): void {
        this.clipboard = items
    }

    duplicateSelected(): void {
        if (this.exploring) return
        const el = elementByKey(this.doc, this.selectedKey)
        if (!el) return
        const off = 200
        let created: string | null = null
        this.mutate(() => {
            const taken = collectLocalIds(this.doc)
            const copy = JSON.parse(JSON.stringify(el)) as EditorElement
            copy.key = nextKey(copy.kind)
            copy.source = { ...copy.source, localId: nextLocalId(copy.kind, taken) }
            // A duplicate is a *new* element: it always creates its own asset
            // rather than pointing a second geometry at the original one —
            // except a run, which continues the same network (spec
            // 2026-10-20-33): it keeps its network asset, pending or saved.
            if (copy.owner.newAsset && copy.kind !== 'run' && copy.kind !== 'riser') {
                copy.owner = { ...copy.owner, newAsset: { ...copy.owner.newAsset, key: nextKey('asset') } }
            }
            if (copy.kind === 'wall') {
                copy.start = { x: copy.start.x + off, y: copy.start.y + off }
                copy.end = { x: copy.end.x + off, y: copy.end.y + off }
            } else if (copy.kind === 'space') {
                copy.boundary = copy.boundary.map((p) => ({ x: p.x + off, y: p.y + off }))
                if (copy.holes) copy.holes = copy.holes.map((h) => h.map((p) => ({ x: p.x + off, y: p.y + off })))
            } else if (copy.kind === 'opening') {
                copy.center = { x: copy.center.x + off, y: copy.center.y + off }
                copy.wallKey = undefined
            } else if (copy.kind === 'run') {
                copy.points = copy.points.map((p) => ({ x: p.x + off, y: p.y + off }))
            } else if (copy.kind === 'riser') {
                copy.position = { x: copy.position.x + off, y: copy.position.y + off }
            } else {
                copy.position = { x: copy.position.x + off, y: copy.position.y + off }
            }
            this.doc.elements.push(copy)
            created = copy.key
        })
        if (created) this.select(created)
    }

    /** Applies an inspector edit inside one undo step. */
    updateElement(key: string, patch: (el: EditorElement) => void): void {
        const el = elementByKey(this.doc, key)
        if (!el) return
        this.mutate(() => {
            patch(el)
            if (el.kind === 'wall') this.reprojectOpenings(el)
        })
    }

    /* ------------------------------------------------------------------- view */

    /** The reachable zoom range, anchored on the current document's fit scale. */
    private zoomLimits(): { min: number; max: number } {
        const r = this.svg?.getBoundingClientRect()
        // Unmounted or not laid out yet: anchor on the current scale so a zoom
        // still moves the view instead of snapping to an unrelated bound.
        const fit = r && r.width >= 40 && r.height >= 40
            ? fitScale(r, bboxOfPoints(documentPoints(this.doc)))
            : this.view.k
        return zoomRange(fit)
    }

    fitView(): void {
        const r = this.svg?.getBoundingClientRect()
        if (!r || r.width < 40 || r.height < 40) return
        const bb = bboxOfPoints(documentPoints(this.doc))
        if (!bb) {
            // Empty scene: 20 m across, centred — something to draw onto.
            this.view = { k: fitScale(r, null), x: r.width / 2, y: r.height / 2 }
            this.render()
            return
        }
        const w = Math.max(bb.x1 - bb.x0, 1)
        const h = Math.max(bb.y1 - bb.y0, 1)
        const k = fitScale(r, bb) * FIT_MARGIN
        this.view = {
            k,
            x: (r.width - w * k) / 2 - bb.x0 * k,
            y: (r.height - h * k) / 2 - bb.y0 * k,
        }
        this.render()
    }

    /**
     * Frame these elements, the way {@link fitView} frames the whole document:
     * a lot opened on its floor's plan has no outline of its own, so the view
     * lands on the rooms drawn under it. A region of the document, hence the
     * tight fit; clamped into the document's zoom range.
     */
    fitElements(keys: string[]): void {
        const r = this.svg?.getBoundingClientRect()
        if (!r || r.width < 40 || r.height < 40) return
        const bb = bboxOfPoints(keys.flatMap((key) => {
            const el = elementByKey(this.doc, key)
            return el ? elementPoints(el) : []
        }))
        if (!bb) return
        const { k: raw, w, h } = fitBox(r, bb, true)
        const { min, max } = this.zoomLimits()
        const k = clamp(raw * FIT_MARGIN, min, max)
        this.view = {
            k,
            x: (r.width - w * k) / 2 - bb.x0 * k,
            y: (r.height - h * k) / 2 - bb.y0 * k,
        }
        this.render()
    }

    zoomBy(factor: number): void {
        const r = this.svg?.getBoundingClientRect()
        const cx = (r?.width ?? 0) / 2
        const cy = (r?.height ?? 0) / 2
        this.zoomAt({ x: cx, y: cy }, factor)
    }

    /**
     * What the viewport is looking at, in the document's own terms: the
     * millimetre point under the middle of the stage, and the scale.
     *
     * Deliberately *not* `this.view`, which is a pixel offset and a scale — a
     * pair that only means anything alongside the stage's current size. The
     * centre and the zoom survive a different window, a collapsed panel and a
     * phone, which is what makes them worth putting in a URL.
     */
    viewport(): PlanViewport {
        const r = this.svg?.getBoundingClientRect()
        const c = this.scrToMm({ x: (r?.width ?? 0) / 2, y: (r?.height ?? 0) / 2 })
        return { cx: c.x, cy: c.y, k: this.view.k }
    }

    /**
     * Look at that point, at that scale — the inverse of {@link viewport}.
     *
     * A scale outside the document's reach is clamped rather than refused: a
     * link written on a wide screen is still a link on a narrow one, and a
     * slightly different magnification is a far better answer than none.
     * Returns false only when the stage has no size yet — the caller then keeps
     * whatever the fit produced instead of framing on nothing.
     */
    setViewport(v: Partial<PlanViewport>): boolean {
        const r = this.svg?.getBoundingClientRect()
        if (!r || r.width < 40 || r.height < 40) return false
        const { min, max } = this.zoomLimits()
        const k = v.k !== undefined ? clamp(v.k, min, max) : this.view.k
        // Keep the current centre for whichever half was not supplied.
        const now = this.viewport()
        const cx = v.cx ?? now.cx
        const cy = v.cy ?? now.cy
        this.view = { k, x: r.width / 2 - cx * k, y: r.height / 2 - cy * k }
        this.render()
        return true
    }

    private zoomAt(scr: Point, factor: number): void {
        const { min, max } = this.zoomLimits()
        const k2 = clamp(this.view.k * factor, min, max)
        if (k2 === this.view.k) return
        this.view.x = scr.x - (scr.x - this.view.x) * (k2 / this.view.k)
        this.view.y = scr.y - (scr.y - this.view.y) * (k2 / this.view.k)
        this.view.k = k2
        this.render()
    }

    panTo(key: string): void {
        const el = elementByKey(this.doc, key)
        const r = this.svg?.getBoundingClientRect()
        if (!el || !r) return
        const bb = bboxOfPoints(elementPoints(el))
        if (!bb) return
        const c = this.mmToScr({ x: (bb.x0 + bb.x1) / 2, y: (bb.y0 + bb.y1) / 2 })
        this.view.x += r.width / 2 - c.x
        this.view.y += r.height / 2 - c.y
        this.render()
    }

    private onWheel = (e: WheelEvent): void => {
        e.preventDefault()
        this.zoomAt(this.eventScr(e), Math.exp(-e.deltaY * 0.0015))
    }

    /**
     * The second click of a pair. Returns true when it consumed the click, so
     * the caller skips the ordinary single-click handling.
     */
    private handleDoubleClick(eventTarget: EventTarget | null, scr: Point): boolean {
        if (this.tool === 'wall') {
            this.endWallChain()
            return true
        }
        if (this.tool === 'run') {
            if (!this.drawing) return false
            this.finishRun()
            return true
        }
        // Both polygon tools: a double click ends the shape where it is, with
        // no trip back to the first point to close the ring by hand.
        if (this.tool === 'room' || this.tool === 'unit') {
            if (!this.drawing) return false
            this.closeRoom()
            return true
        }
        if (this.tool === 'hole') {
            if (!this.drawing) return false
            this.closeHole()
            return true
        }
        if (this.tool !== 'select') return false
        const target = (eventTarget as Element | null)?.closest?.('[data-kind="space"]') as HTMLElement | null
        // Away from every shape, or on a grouper's interior (which is not a DOM
        // target at all): the outline under the pointer is what was aimed at.
        const el = target
            ? elementByKey(this.doc, target.dataset.key)
            : elementByKey(this.doc, groupersAt(
                this.doc,
                this.scrToMm(scr),
                SNAP_TOLERANCE_PX / Math.max(this.view.k, 1e-9),
            )[0]?.key)
        if (!el || el.kind !== 'space' || el.boundary.length < 3) return false
        const mm = this.scrToMm(scr)
        let best = -1
        // The ring the nearest edge belongs to: -1 for the boundary, else a hole.
        let bestRing = -1
        let bestD = 20 / Math.max(this.view.k, 1e-9)
        const rings = [el.boundary, ...spaceHoles(el)]
        rings.forEach((ring, r) => {
            for (let i = 0; i < ring.length; i++) {
                const pr = projectToSegment(mm, ring[i], ring[(i + 1) % ring.length])
                if (pr.d < bestD) {
                    bestD = pr.d
                    best = i
                    bestRing = r - 1
                }
            }
        })
        // On an **edge** — a hole's as much as the boundary's — a double click
        // inserts a vertex: the shape is what was aimed at. Anywhere else
        // inside the shape it is the *name* that was aimed at: double-clicking
        // a room or a lot is how everyone expects to rename one.
        if (best >= 0) {
            const ring = rings[bestRing + 1]
            this.mutate(() => ring.splice(best + 1, 0, mm))
            this.select(el.key, best + 1)
            if (bestRing >= 0) {
                this.selectedHole = bestRing
                this.render()
            }
            return true
        }
        this.select(el.key)
        this.hooks.onRenameRequested?.(el.key)
        return true
    }

    /* -------------------------------------------------------------- keyboard */

    private onKeyDown = (e: KeyboardEvent): void => {
        if (e.key === 'Alt') this.altDown = true
        if (e.key === 'Shift') this.shiftDown = true
        // Save first, before the typing guard below: Cmd+S has to work while a
        // name or a code is still under the cursor, and the browser's "save
        // this page" is never what anyone wants here. The fields commit as
        // they are typed, so what lands is what is on screen.
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
            e.preventDefault()
            this.hooks.onSaveRequested?.()
            return
        }
        const active = document.activeElement
        const inField = !!active && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName)
        if (inField) {
            if (e.key === 'Escape') (active as HTMLElement).blur()
            return
        }
        if (e.key === ' ') {
            this.spaceDown = true
            e.preventDefault()
        }
        const mod = e.metaKey || e.ctrlKey
        if (mod && e.key.toLowerCase() === 'z') {
            e.preventDefault()
            if (e.shiftKey) this.redo()
            else this.undo()
            return
        }
        if (mod && e.key.toLowerCase() === 'y') {
            e.preventDefault()
            this.redo()
            return
        }
        if (mod && e.key.toLowerCase() === 'c') {
            // Only when there is something to take: an empty selection must
            // leave the browser's own copy alone.
            if (this.copySelection() > 0) e.preventDefault()
            return
        }
        if (mod && e.key.toLowerCase() === 'x') {
            // Cut is copy *then* delete, on exactly what copy takes. Gated on
            // the copy having taken something: a wall or a room is not
            // copyable, and a Ctrl+X that deleted one without filling the
            // clipboard would be a silent Delete with nothing to paste back.
            if (this.copySelection() > 0) {
                e.preventDefault()
                this.deleteSelected()
            }
            return
        }
        if (mod && e.key.toLowerCase() === 'v') {
            if (this.pasteClipboard() > 0) e.preventDefault()
            return
        }
        if (mod && e.key.toLowerCase() === 'd') {
            e.preventDefault()
            this.duplicateSelected()
            return
        }
        if (mod) return
        switch (e.key) {
            case 'v': case 'V': this.setTool('pan'); break
            case 's': case 'S': this.setTool('select'); break
            case 'w': case 'W': this.setTool('wall'); break
            case 'r': case 'R': this.setTool('room'); break
            case 'u': case 'U': this.setTool('unit'); break
            case 'h': case 'H': this.setTool('hole'); break
            case 'd': case 'D': this.setTool('door'); break
            case 'n': case 'N': this.setTool('window'); break
            case 'e': case 'E': this.setTool('equipment'); break
            case 'l': case 'L': this.setTool('run'); break
            case 'k': case 'K': this.setTool('riser'); break
            case 'c': case 'C': this.setTool('calibrate'); break
            case 'm': case 'M': if (this.tool === 'calibrate') this.startMeasure(); break
            case 'f': case 'F': this.fitView(); break
            case 'Enter':
                if (this.drawing?.kind === 'room') this.closeRoom()
                else if (this.drawing?.kind === 'hole') this.closeHole()
                else if (this.drawing?.kind === 'wall') this.endWallChain()
                else if (this.drawing?.kind === 'run') this.finishRun()
                else if (this.tool === 'calibrate') this.hooks.onCalibrationCommit?.()
                break
            case 'Escape':
                // One rung down the ladder per press, most committed state
                // first: the run being drawn, the calibration, the selection,
                // the layer, then the armed tool — select, and then the hand,
                // where the editor can do nothing at all. Idle, that is the two
                // presses the request asks for; with work in hand it backs out
                // of that first, which is what Escape has always done here.
                //
                // The hand is the floor of the ladder: pressing Escape there
                // stays there rather than climbing back to select, because a
                // ladder that runs in both directions is a toggle.
                if (this.drag?.type === 'marquee') {
                    // A rubber band in flight is the most transient thing on
                    // screen, so it goes first — and it leaves the selection
                    // exactly as it found it (§4).
                    this.drag = null
                    this.marquee = null
                    this.render()
                } else if (this.drawing) {
                    this.drawing = null
                    this.render()
                } else if (this.tool === 'calibrate' && this.calibrationPending) {
                    this.cancelCalibration()
                } else if (this.selectedKey) {
                    this.select(null)
                } else if (this.selectedLayerUid) {
                    this.selectLayer(null)
                } else if (this.tool !== 'select' && this.tool !== 'pan') {
                    this.setTool('select')
                } else if (this.tool === 'select') {
                    this.setTool('pan')
                }
                break
            case 'Delete': case 'Backspace':
                e.preventDefault()
                this.deleteSelected()
                break
            case 'ArrowLeft': case 'ArrowRight': case 'ArrowUp': case 'ArrowDown': {
                e.preventDefault()
                const dx = (e.key === 'ArrowRight' ? 1 : 0) - (e.key === 'ArrowLeft' ? 1 : 0)
                const dy = (e.key === 'ArrowDown' ? 1 : 0) - (e.key === 'ArrowUp' ? 1 : 0)
                // The arrows pan the view — always, selection or not. Moving
                // geometry is what **Alt** is for: a plan is read far more
                // often than it is edited, and an arrow key that quietly
                // displaced whatever happened to be selected moved walls
                // nobody meant to touch. Shift is the coarse step in both
                // cases, and the nudge is in screen pixels so it feels the same
                // at every zoom.
                if (e.altKey && this.selectedKey && !this.exploring) {
                    this.nudgeSelected(dx, dy, e.shiftKey ? NUDGE_COARSE_PX : NUDGE_PX)
                } else {
                    this.panBy(-dx * PAN_STEP_PX * (e.shiftKey ? 4 : 1), -dy * PAN_STEP_PX * (e.shiftKey ? 4 : 1))
                }
                break
            }
            default:
                break
        }
    }

    private onKeyUp = (e: KeyboardEvent): void => {
        if (e.key === 'Alt') this.altDown = false
        if (e.key === 'Shift') this.shiftDown = false
        if (e.key === ' ') this.spaceDown = false
    }

    /* ------------------------------------------------------------------ misc */

    /** A snapshot of the loaded document, for diffing at save time. */
    static baseline(doc: SceneDoc): SceneDoc {
        return cloneDoc(doc)
    }

    /** The room containing a point — exposed for the shell's owner resolution. */
    spaceAt(p: Point) {
        return containingSpace(this.doc, p)
    }
}
